<template>
  <v-row v-if="error.getFilesError" no-gutters align="center" justify="center">
    <v-card width="800" height="1000" class="my-16" flat>
      <v-row no-gutters justify="center" align="center">
        <v-col
          cols="8"
          class="d-flex justify-start align-start mb-3 flex-column"
        >
          <v-row class="flex-column" justify="center" align="center" no-gutters>
            <v-card flat height="25">
              <span class="footerText align-text d-block">
                {{ error.shownErrMsg }}
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon size="18" v-bind="attrs" v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <v-container
                    class="text-body-2 pa-0"
                    style="max-width: 230px"
                  >
                    {{ error.tooltipErrMsg }}
                  </v-container>
                </v-tooltip>
              </span>
            </v-card>
          </v-row>
          <br />
          <span class="text-subheading">
            {{ $t('message.videoApiErrMsg') }} <br />
            <button
              type="button"
              class="primary--text"
              @click="handleStatusPage"
            >
              <span class="primary--text">
                {{ $t('message.checkStatusPageLink') }}
              </span>
            </button>
          </span>
          <br />
        </v-col>
        <v-col cols="12">
          <v-row no-gutters align="center" justify="center">
            <v-btn
              color="primary"
              class="ma-2 ml-3 white--text"
              rounded
              @click="readAllFiles"
            >
              <span class="mx-2">{{ $t('message.tryAgain') }}</span>
              <v-icon right dark> mdi-cached </v-icon>
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </v-row>

  <page-view
    v-else
    :is-loading="isFetching"
    class="page-videos mousearea"
    fill-height
    @[mousedownEvent]="handleMousedown"
    @[mousemoveEvent]="handleMousemove"
    @[mouseupEvent]="handleMouseup"
    @[mouseleaveEvent]="handleMouseleave"
  >
    <template #header>
      <base-header
        :title="pageTitle"
        :subtitle="pageSubtitle"
        :menu="pageMenu"
        :folders-tree="foldersTree.ids"
        :folders-tree-threshold="foldersTree.threshold"
        :has-intro="!isMobileBreakpoint"
        @title="isFolder && handleDoubleClickRow(lastFolder)"
        @back="handleRoot"
        @folders="handleFolders"
      >
        <template #actions>
          <template v-if="hasEditVideoPerm">
            <base-btn
              :data-intro="`<strong>${$t(
                'message.introJS.videos.folderTitle'
              )}</strong><br><br>${$t('message.introJS.videos.folder')}`"
              type="tertiary"
              @click="handleCreateFolder"
            >
              <span class="responsive-text">
                {{ $t('message.newFolder') }}
              </span>
            </base-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: on, attrs: attrs }">
                <div
                  v-bind="verifyPermissions(permissions) ? attrs : false"
                  v-on="verifyPermissions(permissions) ? on : false"
                >
                  <base-btn
                    @click="
                      !isBeta && !isDotCom
                        ? handleImportModal()
                        : $root.$emit('openCreateVideoModal')
                    "
                    :disabled="
                      !isLoadedPlanInfo ||
                      !checkIfSubscriptionIsActive ||
                      verifyPermissions(permissions)
                    "
                    :data-intro="`<strong>${$t(
                      'message.introJS.videos.uploadTitle'
                    )}</strong><br><br>${$t('message.introJS.videos.upload')}`"
                  >
                    <span class="responsive-text">
                      {{ $t('message.importVideos') }}
                    </span>
                  </base-btn>
                </div>
              </template>
              <span>{{ $t('message.noPermissionToDo') }}</span>
            </v-tooltip>
          </template>
          <template v-else>
            <v-card width="250px"></v-card>
          </template>
        </template>
      </base-header>
    </template>

    <template #preContent v-if="displayBanner && !isFetching">
      <AiBanner
        v-if="showAiBanner"
        @close="
          showAiBanner = false
          resizeTimeout()
        "
      />
      <TrainingBanner
        v-if="
          showTrainingBanner &&
          isPtBr &&
          !isPandaUs &&
          !showBlackFridayBanner &&
          !bannersExpiredDelay
        "
        @close="
          showTrainingBanner = false
          resizeTimeout()
          bannersDelay()
        "
      />
      <BlackFridayBanner
        v-if="
          showBlackFridayBanner && !showTrainingBanner && !bannersExpiredDelay
        "
        @close="
          showBlackFridayBanner = false
          resizeTimeout()
          bannersDelay()
        "
      />
      <RecapBanner
        v-if="showRecapBanner && isBeta"
        @close="
          showRecapBanner = false
          resizeTimeout()
        "
        @openRecapModal="openRecapModal = true"
      />
    </template>
    <template #content>
      <input
        type="file"
        accept="video/*"
        class="uploadInput"
        id="uploadInput"
        ref="uploadInput"
        multiple
      />
      <v-col v-if="pageSubtitle">
        <span
          class="body-bold-small"
          v-if="foldersTree.ids.size < foldersTree.threshold"
        >
          <span class="router-link" @click="emitBack">
            <v-icon size="16" class="textType3--text mr-1"> panda-back </v-icon>
            {{ $t('message.pages.video.pageMenu.videos') }}
          </span>
          <span class="textType6--text body-regular-small">
            / {{ folder.name }}
          </span></span
        >

        <span
          v-if="foldersTree.ids.size >= foldersTree.threshold"
          class="body-bold-small"
        >
          <span class="router-link" @click="emitBackFolder">
            <v-icon size="16" class="textType3--text mr-1"> panda-back </v-icon>
            {{ lastFolder.name }}
          </span>
          <span class="textType6--text body-regular-small">
            / {{ folder.name }}</span
          >
        </span>
      </v-col>
      <v-col
        :cols="isMobileBreakpoint || isTabletBreakpoint ? 12 : 'auto'"
        :order="isMobileBreakpoint || isTabletBreakpoint ? 'first' : ''"
        class="ml-auto d-flex justify-space-between mr-4"
      >
        <SearchBar
          v-show="!isMobileBreakpoint"
          :list="searchList"
          :fuseOptions="searchFuseOptions"
          :placeholder="
            $t('message.pages.videosDataTable.searchBar.placeholder')
          "
          @filterList="($value) => (filterList = $value)"
        />
        <span class="body-regular-smaller textType6--text">
          {{ pageInfo }}
        </span>
      </v-col>
      <!-- data-table::header -->
      <v-card
        v-show="tableHeaderVisible"
        flat
        width="100%"
        class="data-table__header"
        ref="dataTableHeader"
        :data-intro="`<strong>${$t(
          'message.introJS.videos.allItemsTitle'
        )}</strong><br><br>${$t('message.introJS.videos.allItems')}`"
      >
        <v-row no-gutters>
          <v-card class="d-flex align-center justify-center" width="50px" flat>
            <v-simple-checkbox
              v-if="hasEditVideoPerm"
              :color="!checkboxIndeterminateStatus ? 'primary' : ''"
              :indeterminate="checkboxIndeterminateStatus"
              :ripple="false"
              :value="anyItemSelected"
              :data-intro="`<strong>${$t(
                'message.introJS.videos.selectItemTitle'
              )}</strong><br><br>${$t('message.introJS.videos.selectItem')}`"
              @input="selectAllItems"
            />
          </v-card>
          <v-col class="d-flex align-center justify-start">
            <v-row no-gutters>
              <v-hover v-slot="{ hover }">
                <div class="d-flex align-center" @click="handleOrderByName">
                  <span class="caption mr-1">
                    {{ $t('message.pages.videosDataTable.header.name') }}
                  </span>
                  <v-icon v-if="hover && !nameOrder" class="menuIcon"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && nameOrder === 'desc'" class="menuIcon"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && nameOrder === 'asc'" class="menuIcon"
                    >mdi-menu-up</v-icon
                  >
                </div>
              </v-hover>
            </v-row>
          </v-col>
          <v-card
            v-if="columns[0].visible"
            class="d-flex align-center"
            height="45px"
            flat
            :style="
              isPtBr
                ? columns[1].visible === false && columns[2].visible === false
                  ? 'width: 130px'
                  : 'width: 96px'
                : isEn
                ? columns[1].visible === false && columns[2].visible === false
                  ? 'width: 150px'
                  : 'width: 96px'
                : isEs
                ? columns[1].visible === false && columns[2].visible === false
                  ? 'width: 150px'
                  : 'width: 96px'
                : 'width: 96px'
            "
          >
            <v-hover v-slot="{ hover }">
              <div class="d-flex align-center" @click="handleOrderByLang">
                <span class="caption pr-1">
                  {{ $i18next.t('message.ai.language') }}
                </span>
                <v-icon v-if="hover && !langOrder" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && langOrder === 'desc'" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && langOrder === 'asc'" class="menuIcon"
                  >mdi-menu-up</v-icon
                >
              </div>
            </v-hover>
          </v-card>
          <v-card
            v-if="columns[1].visible"
            class="d-flex align-center"
            height="45px"
            flat
            width="96px"
            :style="
              columns[2].visible
                ? 'width: 96px'
                : isPtBr
                ? 'width: 130px'
                : 'width: 150px'
            "
          >
            <v-hover v-slot="{ hover }">
              <div class="d-flex align-center" @click="handleOrderByLang">
                <span class="caption" style="margin-right: 4px">
                  {{ $i18next.t('message.ai.subtitles.subtitle') }}
                </span>
                <v-icon v-if="hover && !langOrder" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && langOrder === 'desc'" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && langOrder === 'asc'" class="menuIcon"
                  >mdi-menu-up</v-icon
                >
              </div>
            </v-hover>
          </v-card>
          <v-card
            v-if="columns[2].visible"
            class="d-flex align-center pl-0"
            height="45px"
            flat
            :style="isPtBr ? 'width: 130px' : 'width: 155px'"
          >
            <v-hover v-slot="{ hover }">
              <div class="d-flex align-center" @click="handleOrderByLang">
                <span class="caption pr-1">
                  {{ $i18next.t('message.ai.ctaModal.dubbings.title') }}
                </span>
                <v-icon v-if="hover && !langOrder" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && langOrder === 'desc'" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && langOrder === 'asc'" class="menuIcon"
                  >mdi-menu-up</v-icon
                >
              </div>
            </v-hover>
          </v-card>
          <v-card
            v-if="columns[3].visible"
            class="d-flex align-center"
            height="45px"
            flat
            width="96px"
            :style="
              !columns[4].visible
                ? 'width: 75px'
                : isEs
                ? 'width: 90px'
                : 'width: 96px'
            "
          >
            <v-hover v-slot="{ hover }">
              <div
                class="d-flex align-center justify-end"
                @click="handleOrderBySize"
              >
                <span class="caption mr-1">
                  {{ $t('message.pages.videosDataTable.header.size') }}
                  <v-icon v-if="hover && !sizeOrder" class="menuIcon"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && sizeOrder === 'desc'" class="menuIcon"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && sizeOrder === 'asc'" class="menuIcon"
                    >mdi-menu-up</v-icon
                  >
                </span>
              </div>
            </v-hover>
          </v-card>
          <v-card
            v-if="columns[4].visible"
            class="d-flex align-center"
            height="45px"
            flat
            :style="isPtBr ? 'width: 96px' : 'width: 75px'"
          >
            <v-hover v-slot="{ hover }">
              <div
                class="d-flex align-center justify-end"
                @click="handleOrderByTime"
              >
                <span class="caption pr-1">
                  {{ $t('message.pages.videosDataTable.header.time') }}
                </span>
                <v-icon v-if="hover && !timeOrder" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && timeOrder === 'desc'" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && timeOrder === 'asc'" class="menuIcon"
                  >mdi-menu-up</v-icon
                >
              </div>
            </v-hover>
          </v-card>
          <v-card
            v-if="columns[5].visible"
            class="d-flex align-center"
            height="45px"
            :style="isPtBr ? 'width: 114px' : 'width: 114px'"
            flat
          >
            <v-hover v-slot="{ hover }">
              <div
                class="d-flex align-center justify-end"
                @click="handleOrderByLastModified"
              >
                <span class="caption mr-1">
                  {{
                    $t('message.pages.videosDataTable.header.lastModification')
                  }}
                </span>
                <v-icon v-if="hover && !lastModifiedOrder" class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon
                  v-if="hover && lastModifiedOrder === 'desc'"
                  class="menuIcon"
                  >mdi-menu-down</v-icon
                >
                <v-icon
                  v-if="hover && lastModifiedOrder === 'asc'"
                  class="menuIcon"
                  >mdi-menu-up</v-icon
                >
              </div>
            </v-hover>
          </v-card>
          <v-card class="d-flex align-center pr-8 pl-2" height="45px" flat>
            <span
              @click="openFilterMenu"
              :data-intro="`<strong>${$t(
                'message.introJS.videos.exibitionTitle'
              )}</strong><br><br>${$t('message.introJS.videos.exibition')}`"
            >
              <v-icon class="columnIcon" size="16"> panda-columns </v-icon>
            </span>
          </v-card>
          <v-menu
            v-model="menuVisible"
            :position-x="menuX"
            :position-y="menuY"
            absolute
            offset-y
            :close-on-content-click="false"
          >
            <v-list
              :style="`max-height: ${availableSpaceBelow}px !important; min-width: 150px;`"
              class="menu-list"
            >
              <v-list-item
                v-for="(column, index) in columns"
                :key="index"
                @click="toggleColumnVisibility(column)"
              >
                <v-list-item-action class="mr-0">
                  <v-icon size="16" v-if="column.visible">
                    panda-check-outline
                  </v-icon>
                </v-list-item-action>
                <v-list-item-title>{{ column.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-row>
      </v-card>
      <!-- data-table::header-full-width -->
      <portal-target
        id="tooltip-portal"
        name="flagTooltipPortal"
      ></portal-target>
      <!-- drop -->
      <drop-area
        :current-folder="currentFolderName"
        :handle-add-file="handleAddFile"
        :unbind-event="isDragging"
        can-highlight
        @unbindEvent="mArea.isDragging = $event"
        style="position: relative; z-index: 1"
      >
        <!-- data-table -->
        <vue-virtual-table
          v-if="allItems.length > 0"
          ref="dataTableContent"
          class="data-table__content"
          :class="{ virtualTable: $vuetify.theme.dark }"
          :config="tableConfig"
          :data="allItems"
          :height="tableHeight"
          :itemHeight="48"
          :minWidth="300"
          @contextmenu="show"
          @click="handleClickRow"
          style="position: relative; z-index: 1"
        >
          <template slot-scope="scope" slot="selectCommon">
            <v-row
              no-gutters
              justify="center"
              class="data-index"
              align="center"
              v-observe-visibility="
                (isVisible, entry) => visibilityChanged(isVisible, scope.row)
              "
              :class="{
                rowIsSelected: checkIfSelected(scope.row),
                rowIsDragged: checkIfSelected(scope.row) && isDragging,
              }"
              :data-index="scope.index"
              @dragend="handleDragEnd"
              @[dragstartEvent]="handleDragStart($event, scope.row)"
              @[dragenterEvent]="handleDragEnter($event)"
              @[dragoverEvent]="handleDragOver($event)"
              @[dragleaveEvent]="handleDragLeave($event)"
              @[dropEvent]="handleDrop($event, scope.row)"
            >
              <v-simple-checkbox
                v-if="!scope.row.type && hasEditVideoPerm"
                color="primary"
                :ripple="false"
                :value="checkIfSelected(scope.row)"
                @input="checkboxSelection(scope.row)"
                @click="setKeySelectIndex(scope.index)"
              />
            </v-row>
          </template>
          <template slot-scope="scope" slot="drmColumn">
            <v-row
              justify="center"
              no-gutters
              :class="{
                rowIsSelected: checkIfSelected(scope.row),
                rowIsDragged: checkIfSelected(scope.row) && isDragging,
              }"
              @[dragenterEvent]="handleDragEnter($event)"
              @[dragoverEvent]="handleDragOver($event)"
              @[dragleaveEvent]="handleDragLeave($event)"
              @[dropEvent]="handleDrop($event, scope.row)"
              @click="handleDrmMenu(scope.row)"
              style="cursor: pointer"
            >
              <v-icon v-if="!scope.row.drm_group_ids" size="16">
                panda-drm-off
              </v-icon>

              <template v-else>
                <v-icon
                  v-if="isAnyDrmGroupActive(scope.row.drm_group_ids)"
                  size="16"
                >
                  panda-drm-on
                </v-icon>

                <v-icon v-else size="16"> panda-drm-on </v-icon>
              </template>
            </v-row>
          </template>
          <template slot-scope="scope" slot="drmColumnSpacer">
            <v-row
              justify="center"
              no-gutters
              :class="{
                rowIsSelected: checkIfSelected(scope.row),
                rowIsDragged: checkIfSelected(scope.row) && isDragging,
              }"
              @[dragenterEvent]="handleDragEnter($event)"
              @[dragoverEvent]="handleDragOver($event)"
              @[dragleaveEvent]="handleDragLeave($event)"
              @[dropEvent]="handleDrop($event, scope.row)"
              @click="handleDrmMenu(scope.row)"
              style="cursor: pointer"
            >
            </v-row>
          </template>
          <template slot-scope="scope" slot="dotsColumn">
            <v-row
              no-gutters
              :class="{
                rowIsSelected: checkIfSelected(scope.row),
                rowIsDragged: checkIfSelected(scope.row) && isDragging,
              }"
              @[dragenterEvent]="handleDragEnter($event)"
              @[dragoverEvent]="handleDragOver($event)"
              @[dragleaveEvent]="handleDragLeave($event)"
              @[dropEvent]="handleDrop($event, scope.row)"
              @click="show(scope.row, $event)"
              style="cursor: pointer"
            >
              <span
                class="dotsRow"
                :class="{
                  dotsActive: showMenu && menuItem.id === scope.row.id,
                }"
              >
                <span
                  v-if="scope.index === 0"
                  :data-intro="`<strong>${$t(
                    'message.introJS.videos.folderTitle'
                  )}</strong><br><br>${$t('message.introJS.videos.folder')}`"
                >
                  <v-icon size="16"> panda-menu-action </v-icon>
                </span>
                <span v-else>
                  <v-icon size="16"> panda-menu-action </v-icon>
                </span>
              </span>
            </v-row>
          </template>
          <template slot-scope="scope" slot="titleCommon">
            <v-row
              no-gutters
              style="position: relative; max-width: 100%"
              align="center"
              class="data-index"
              :draggable="isDragging"
              :class="{
                rowIsSelected: checkIfSelected(scope.row),
                rowIsDragged: checkIfSelected(scope.row) && isDragging,
              }"
              :data-index="scope.index"
              @click="handleClickRow(scope.row)"
              @dragend="handleDragEnd"
              @[dragstartEvent]="handleDragStart($event, scope.row)"
              @[dragenterEvent]="handleDragEnter($event)"
              @[dragoverEvent]="handleDragOver($event)"
              @[dragleaveEvent]="handleDragLeave($event)"
              @[dropEvent]="handleDrop($event, scope.row)"
              @mousemove="handlePreviewMousemove($event, scope.row)"
              @mouseleave="
                ($event) => handlePreviewMouseleave($event, scope.row)
              "
            >
              <template v-if="scope.row.name">
                <v-row
                  no-gutters
                  align="center"
                  style="max-width: 100%; margin-top: 5px"
                >
                  <v-icon size="16px" style="margin-right: 8px" color="#ABADBA">
                    mdi-folder
                  </v-icon>
                  <span class="text-truncate" :title="scope.row.name">{{
                    scope.row.name
                  }}</span>
                  <span class="pl-1 secondaryText--text caption">
                    {{
                      folderVideosCount[scope.row.id] !== undefined
                        ? `(${folderVideosCount[scope.row.id]})`
                        : folderItemsQuantityData.get(scope.row.id) === '(NaN)'
                        ? '(0)'
                        : folderItemsQuantityData.get(scope.row.id)
                    }}
                  </span>
                </v-row>
              </template>
              <template v-else>
                <v-row
                  no-gutters
                  align="center"
                  style="max-width: 100%"
                  class="video-row"
                >
                  <v-icon
                    size="16"
                    :color="$vuetify.theme.dark ? '#203CF8' : '#7282f3'"
                  >
                    panda-video
                  </v-icon>
                  <div class="ml-2">
                    <template
                      v-if="
                        scope.row.status === 'DRAFT' ||
                        scope.row.status === 'FAILED'
                      "
                    >
                      <span
                        v-if="
                          Math.abs(
                            new Date(scope.row.created_at) - new Date()
                          ) /
                            36e5 >
                          3
                        "
                        :title="scope.row.title"
                        class="text-truncate mt-2"
                      >
                        {{ scope.row.title }}
                      </span>
                      <span
                        v-else
                        :title="scope.row.title"
                        class="text-truncate mt-2"
                      >
                        {{ scope.row.title }}
                      </span>
                    </template>
                  </div>
                  <template v-if="scope.row.status === 'CONVERTING'">
                    <span
                      v-if="
                        Math.abs(new Date(scope.row.created_at) - new Date()) /
                          36e5 <
                        3
                      "
                      class="text-truncate"
                      :title="scope.row.title"
                    >
                      {{ scope.row.title }}
                    </span>
                    <span
                      v-else-if="
                        Math.abs(new Date(scope.row.created_at) - new Date()) /
                          36e5 <
                        48
                      "
                      class="text-truncate"
                      :title="scope.row.title"
                    >
                      {{ scope.row.title }}
                    </span>
                    <span v-else class="text-truncate" :title="scope.row.title">
                      {{ scope.row.title }}
                    </span>
                  </template>
                  <span
                    v-if="scope.row.status === 'BLOCKED'"
                    class="text-truncate"
                    :title="scope.row.title"
                  >
                    {{ scope.row.title }}
                  </span>
                  <span
                    v-if="scope.row.status === 'CONVERTED'"
                    class="text-truncate"
                    :title="scope.row.title"
                  >
                    {{ scope.row.title }}
                  </span>
                  <template v-if="scope.row.status === 'DRAFT'">
                    <v-tooltip
                      bottom
                      v-if="
                        Math.abs(new Date(scope.row.created_at) - new Date()) /
                          36e5 >
                          3 && !checkIfIsImport(scope.row)
                      "
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="on"
                          class="red--text ml-2 noIbar caption"
                        >
                          <v-icon size="20" color="red">
                            mdi-alert-circle-outline
                          </v-icon>
                          {{ $t('message.processingError') }}
                        </span>
                      </template>
                      <span>
                        {{ $t('message.verifyFileFormat') }}
                      </span>
                    </v-tooltip>
                    <v-tooltip bottom persistent :close-delay="500">
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="{
                            ...on,
                            mouseenter: (e) => {
                              tooltipVisible = true
                              on.mouseenter && on.mouseenter(e)
                            },
                          }"
                          class="ml-2 secondaryText--text caption noIbar d-flex align-center"
                        >
                          <div class="mr-2 ml-4 pt-2">
                            <div id="nalp" class="nalps"></div>
                          </div>
                          <span> {{ $t('message.converting') }}</span>
                        </span>
                      </template>
                      <div
                        class="align-center d-flex"
                        style="pointer-events: all !important; width: 310px"
                      >
                        <div style="width: 184px">
                          <span>
                            {{ $t('message.convertingDescription') }}
                          </span>
                        </div>
                        <div
                          style="width: 118px; z-index: 9999; cursor: pointer"
                          class="ml-2"
                        >
                          <base-btn
                            @click.stop="openConvertingLink"
                            type="neutral"
                            size="xsmall"
                          >
                            {{ $t('message.knowMore') }}
                            <v-icon size="16">mdi-open-in-new</v-icon>
                          </base-btn>
                        </div>
                      </div>
                    </v-tooltip>
                  </template>
                  <span v-if="scope.row.status === 'FAILED'">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="on"
                          class="red--text ml-2 noIbar caption"
                        >
                          <v-icon size="20" color="red">
                            mdi-alert-circle-outline
                          </v-icon>
                          {{ $t('message.processingError') }}
                        </span>
                      </template>
                      <span>
                        {{ $t('message.verifyFileFormat') }}
                      </span>
                    </v-tooltip>
                  </span>
                  <template v-if="scope.row.status === 'BLOCKED'">
                    <v-tooltip bottom persistent :close-delay="500">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="d-flex">
                          <span class="blockedWarning ml-4">
                            {{ $t('message.blocked') }}
                          </span>
                        </span>
                      </template>
                      <div
                        class="align-center d-flex"
                        style="pointer-events: all !important; width: 302px"
                      >
                        <div style="width: 184px">
                          <span>
                            {{ $t('message.blockedDescription') }}
                          </span>
                        </div>
                        <div style="width: 118px" cols="4">
                          <base-btn
                            @click.stop="openBlockedLink"
                            type="neutral"
                            size="xsmall"
                          >
                            {{ $t('message.knowMore') }}
                            <v-icon size="16" class="ml-1"
                              >mdi-open-in-new</v-icon
                            >
                          </base-btn>
                        </div>
                      </div>
                    </v-tooltip>
                  </template>
                  <template v-if="scope.row.status === 'CONVERTING'">
                    <v-tooltip
                      v-if="
                        Math.abs(new Date(scope.row.created_at) - new Date()) /
                          36e5 <
                        3
                      "
                      bottom
                      persistent
                      :close-delay="500"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="{
                            ...on,
                            mouseenter: (e) => {
                              tooltipVisible = true
                              on.mouseenter && on.mouseenter(e)
                            },
                          }"
                          class="ml-2 secondaryText--text caption noIbar d-flex align-center"
                        >
                          <div class="mr-2 ml-4 pt-2">
                            <div id="nalp" class="nalps"></div>
                          </div>
                          <span> {{ $t('message.converting') }}</span>
                        </span>
                      </template>
                      <div
                        class="align-center d-flex"
                        style="pointer-events: all !important; width: 310px"
                      >
                        <div style="width: 184px">
                          <span>
                            {{ $t('message.convertingDescription') }}
                          </span>
                        </div>
                        <div
                          class="ml-2"
                          style="width: 118px; z-index: 9999; cursor: pointer"
                          cols="4"
                        >
                          <base-btn
                            @click.stop="openConvertingLink"
                            type="neutral"
                            size="xsmall"
                          >
                            {{ $t('message.knowMore') }}
                            <v-icon size="16" class="ml-1"
                              >mdi-open-in-new</v-icon
                            >
                          </base-btn>
                        </div>
                      </div>
                    </v-tooltip>
                    <span
                      v-else-if="
                        Math.abs(new Date(scope.row.created_at) - new Date()) /
                          36e5 <
                        48
                      "
                      class="ml-2 secondaryText--text caption noIbar"
                    >
                      <v-icon>mdi-progress-upload</v-icon>
                      {{ $t('message.longerThanUsual') }}
                      <span
                        class="primary--text pointer-click"
                        @click="openKnowMoreConverting"
                        >{{ $t('message.knowMore') }} &raquo;
                      </span>
                    </span>
                    <span v-else>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs"
                            v-on="on"
                            class="red--text ml-2 noIbar caption"
                          >
                            <v-icon size="20" color="red">
                              mdi-alert-circle-outline
                            </v-icon>
                            {{ $t('message.processingError') }}
                          </span>
                        </template>
                        <span>
                          {{ $t('message.verifyFileFormat') }}
                        </span>
                      </v-tooltip>
                    </span>
                  </template>
                  <template v-if="scope.row.hasOwnProperty('video_source')">
                    <v-chip class="external-chip no-hover" dense>
                      <v-icon left> mdi-link </v-icon>
                      {{ getExternalLabelValue(scope.row).replace('www.', '') }}
                    </v-chip>
                  </template>
                </v-row>
              </template>
            </v-row>
          </template>
          <template slot-scope="scope" slot="langCommon">
            <template v-if="checkError(scope.row)">
              <v-row
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                  'pl-4': isPtBr,
                  'pl-6': !isPtBr,
                }"
                justify="center"
                class="tooltip-container"
                :style="
                  checkError(scope.row)
                    ? 'height: 48px !important; position: absolute'
                    : ''
                "
              >
                <v-tooltip>
                  <template v-slot:activator="{ props }">
                    <div
                      v-on="props"
                      @mouseenter="handleShowTooltip(scope.row, scope)"
                      @mouseleave="checkMouseLeave(scope.row)"
                      style="width: 60px"
                      class="d-flex justify-content-center"
                    >
                      <simple-svg
                        :src="require('@/assets/warningIcon.svg')"
                        fill-class-name="fill-color"
                        :fill="$vuetify.theme.dark ? 'white' : '#FF8F00'"
                        width="24px"
                        height="24px"
                      />
                      <div
                        v-if="handleShowTooltipIndex(scope.row)"
                        class="tooltip-content"
                        @mouseenter="mouseOverTooltip = true"
                        @mouseleave="mouseOverTooltip = false"
                        style="max-width: 300px"
                        :class="
                          handleTooltipPosition
                            ? 'tooltip-position-bot'
                            : 'tooltip-position-top'
                        "
                      >
                        <span
                          class="font-weight-normal d-flex justify-content-center"
                          style="font-size: 14px"
                          >{{
                            $t('message.ai.subtitles.batchErrorTooltip')
                          }}</span
                        >
                        <v-btn
                          class="mt-1"
                          color="primary"
                          small
                          @click="handleErrorQueue(scope.row)"
                          >OK</v-btn
                        >
                      </div>
                    </div>
                  </template>
                </v-tooltip>
              </v-row>
            </template>
            <template v-else-if="checkStatus(scope.row)">
              <v-row
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                }"
                style="padding-left: 10px"
                justify="start"
              >
                <div class="mr-4 pt-2">
                  <div id="nalp" class="nalps"></div>
                </div>
              </v-row>
            </template>
            <template v-else>
              <v-row
                no-gutters
                justify="start"
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                }"
                @click="handleClickRow(scope.row)"
                @[dragenterEvent]="handleDragEnter($event)"
                @[dragoverEvent]="handleDragOver($event)"
                @[dragleaveEvent]="handleDragLeave($event)"
                @[dropEvent]="handleDrop($event, scope.row)"
                @mouseleave="
                  ($event) => handlePreviewMouseleave($event, scope.row)
                "
              >
                <span v-if="scope.row.original_lang">
                  <FlagTooltip
                    :direction="getTooltipDirection(scope.index)"
                    :items="{
                      original_lang: {
                        label: $i18next.t('message.ai.originalLang'),
                        items: [getPreparedSrcLang(scope.row.original_lang)],
                      },
                    }"
                  >
                    <div class="mt-1">
                      <FlagCards
                        :items="[
                          getCountryCodeBySrcLang(scope.row.original_lang),
                        ]"
                      />
                    </div>
                  </FlagTooltip>
                </span>
                <!-- <span v-else-if="scope.row.title">
                  <div class="flagContainer hoveredClass">
                    <v-icon
                      :color="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                      size="16"
                    >
                      panda-add
                    </v-icon>
                  </div></span
                > -->
              </v-row>
            </template>
          </template>
          <template slot-scope="scope" slot="subCommon">
            <template v-if="checkError(scope.row)">
              <v-row
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                  'pl-4': isPtBr,
                  'pl-6': !isPtBr,
                }"
                justify="center"
                class="tooltip-container"
                :style="
                  checkError(scope.row)
                    ? 'height: 48px !important; position: absolute'
                    : ''
                "
              >
                <v-tooltip>
                  <template v-slot:activator="{ props }">
                    <div
                      v-on="props"
                      @mouseenter="handleShowTooltip(scope.row, scope)"
                      @mouseleave="checkMouseLeave(scope.row)"
                      style="width: 60px"
                      class="d-flex justify-content-center"
                    >
                      <simple-svg
                        :src="require('@/assets/warningIcon.svg')"
                        fill-class-name="fill-color"
                        :fill="$vuetify.theme.dark ? 'white' : '#FF8F00'"
                        width="24px"
                        height="24px"
                      />
                      <div
                        v-if="handleShowTooltipIndex(scope.row)"
                        class="tooltip-content"
                        @mouseenter="mouseOverTooltip = true"
                        @mouseleave="mouseOverTooltip = false"
                        style="max-width: 300px"
                        :class="
                          handleTooltipPosition
                            ? 'tooltip-position-bot'
                            : 'tooltip-position-top'
                        "
                      >
                        <span
                          class="font-weight-normal d-flex justify-content-center"
                          style="font-size: 14px"
                          >{{
                            $t('message.ai.subtitles.batchErrorTooltip')
                          }}</span
                        >
                        <v-btn
                          class="mt-1"
                          color="primary"
                          small
                          @click="handleErrorQueue(scope.row)"
                          >OK</v-btn
                        >
                      </div>
                    </div>
                  </template>
                </v-tooltip>
              </v-row>
            </template>
            <template v-else-if="checkStatus(scope.row)">
              <v-row
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                }"
                style="padding-left: 10px"
                justify="start"
              >
                <div class="mr-2 pt-2">
                  <div id="nalp" class="nalps"></div>
                </div>
              </v-row>
            </template>
            <template v-else>
              <v-row
                no-gutters
                justify="start"
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                }"
                @[dragenterEvent]="handleDragEnter($event)"
                @[dragoverEvent]="handleDragOver($event)"
                @[dragleaveEvent]="handleDragLeave($event)"
                @[dropEvent]="handleDrop($event, scope.row)"
                @mouseleave="
                  ($event) => handlePreviewMouseleave($event, scope.row)
                "
              >
                <span
                  v-if="scope.row.subtitles && scope.row.subtitles.length"
                  class="text-right"
                >
                  <template
                    v-if="scope.row.subtitles && scope.row.subtitles.length"
                  >
                    <div class="d-flex">
                      <FlagTooltip
                        :direction="getTooltipDirection(scope.index)"
                        :items="{
                          subtitles: {
                            label: $i18next.t('message.ai.subtitles.subtitles'),
                            iconFile: 'cc_tooltip',
                            items: scope.row.subtitles.map((item) =>
                              getPreparedSrcLang(item)
                            ),
                          },
                        }"
                      >
                        <div>
                          <div class="d-flex align-center">
                            <FlagCards
                              :couple="true"
                              :items="[
                                ...scope.row.subtitles.map((sub) =>
                                  getCountryCodeBySrcLang(sub)
                                ),
                              ]"
                            />
                          </div>
                        </div>
                      </FlagTooltip>
                    </div>
                  </template>
                </span>
                <span
                  @click="subInListing(scope.row)"
                  v-else-if="scope.row.title"
                >
                  <div class="flagContainer hoveredClass">
                    <v-icon
                      :color="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                      size="16"
                    >
                      panda-add
                    </v-icon>
                  </div></span
                >
              </v-row>
            </template>
          </template>
          <template slot-scope="scope" slot="dubCommon">
            <template v-if="checkError(scope.row)">
              <v-row
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                  'pl-4': isPtBr,
                  'pl-6': !isPtBr,
                }"
                justify="center"
                class="tooltip-container"
                :style="
                  checkError(scope.row)
                    ? 'height: 48px !important; position: absolute'
                    : ''
                "
              >
                <v-tooltip>
                  <template v-slot:activator="{ props }">
                    <div
                      v-on="props"
                      @mouseenter="handleShowTooltip(scope.row, scope)"
                      @mouseleave="checkMouseLeave(scope.row)"
                      style="width: 60px"
                      class="d-flex justify-content-center"
                    >
                      <simple-svg
                        :src="require('@/assets/warningIcon.svg')"
                        fill-class-name="fill-color"
                        :fill="$vuetify.theme.dark ? 'white' : '#FF8F00'"
                        width="24px"
                        height="24px"
                      />
                      <div
                        v-if="handleShowTooltipIndex(scope.row)"
                        class="tooltip-content"
                        @mouseenter="mouseOverTooltip = true"
                        @mouseleave="mouseOverTooltip = false"
                        style="max-width: 300px"
                        :class="
                          handleTooltipPosition
                            ? 'tooltip-position-bot'
                            : 'tooltip-position-top'
                        "
                      >
                        <span
                          class="font-weight-normal d-flex justify-content-center"
                          style="font-size: 14px"
                          >{{
                            $t('message.ai.subtitles.batchErrorTooltip')
                          }}</span
                        >
                        <v-btn
                          class="mt-1"
                          color="primary"
                          small
                          @click="handleErrorQueue(scope.row)"
                          >OK</v-btn
                        >
                      </div>
                    </div>
                  </template>
                </v-tooltip>
              </v-row>
            </template>
            <template v-else-if="checkStatus(scope.row)">
              <v-row
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                }"
                style="padding-left: 10px"
                justify="start"
              >
                <div class="mr-2 pt-2">
                  <div id="nalp" class="nalps"></div>
                </div>
              </v-row>
            </template>
            <template v-else>
              <v-row
                no-gutters
                justify="start"
                :class="{
                  rowIsSelected: checkIfSelected(scope.row),
                  rowIsDragged: checkIfSelected(scope.row) && isDragging,
                }"
                @[dragenterEvent]="handleDragEnter($event)"
                @[dragoverEvent]="handleDragOver($event)"
                @[dragleaveEvent]="handleDragLeave($event)"
                @[dropEvent]="handleDrop($event, scope.row)"
                @mouseleave="
                  ($event) => handlePreviewMouseleave($event, scope.row)
                "
              >
                <span
                  v-if="scope.row.audios && scope.row.audios.length"
                  class="text-right"
                >
                  <template v-if="scope.row.audios && scope.row.audios.length">
                    <div class="d-flex">
                      <FlagTooltip
                        :direction="getTooltipDirection(scope.index)"
                        :items="{
                          dubbing: {
                            label: $i18next.t('message.ai.dubbings.dubbings'),
                            iconFile: 'dubbing',
                            items: scope.row.audios.map((item) =>
                              getPreparedSrcLang(item)
                            ),
                          },
                        }"
                      >
                        <div>
                          <div class="d-flex align-center">
                            <FlagCards
                              :couple="true"
                              :items="[
                                ...scope.row.audios.map((audio) =>
                                  getCountryCodeBySrcLang(audio)
                                ),
                              ]"
                            />
                          </div>
                        </div>
                      </FlagTooltip>
                    </div>
                  </template>
                </span>
                <span
                  v-else-if="scope.row.title"
                  @click="dubInListing(scope.row)"
                >
                  <div class="flagContainer hoveredClass">
                    <v-icon
                      :color="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                      size="16"
                    >
                      panda-add
                    </v-icon>
                  </div></span
                >
              </v-row>
            </template>
          </template>
          <template slot-scope="scope" slot="sizeCommom">
            <v-row
              no-gutters
              justify="end"
              class="px-2 itemDesc"
              :class="{
                rowIsSelected: checkIfSelected(scope.row),
                rowIsDragged: checkIfSelected(scope.row) && isDragging,
              }"
              @click="handleClickRow(scope.row)"
              @[dragenterEvent]="handleDragEnter($event)"
              @[dragoverEvent]="handleDragOver($event)"
              @[dragleaveEvent]="handleDragLeave($event)"
              @[dropEvent]="handleDrop($event, scope.row)"
            >
              <span v-if="scope.row.name">
                {{
                  folderData.get(scope.row.id)
                    ? folderData.get(scope.row.id).size
                      ? calcMegabytes(folderData.get(scope.row.id).size, 'B')
                      : ''
                    : '...'
                }}
              </span>
              <div v-else-if="!!scope.row.type" />
              <span
                v-else-if="
                  scope.row.storage_size &&
                  (scope.row.status === 'CONVERTED' ||
                    scope.row.status === 'CONVERTING' ||
                    scope.row.status === 'BLOCKED')
                "
              >
                <v-tooltip bottom content-class="storageContainer">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{
                        scope.row.storage_size > 0.01
                          ? removeDotIfItExists(
                              calcMegabytes(scope.row.storage_size, 'B')
                            )
                          : ''
                      }}
                    </span>
                  </template>
                  <span>{{ $t('message.storageTooltip') }}</span>
                </v-tooltip>
              </span>
              <span v-else-if="scope.row.hasOwnProperty('video_source')"
                >-</span
              >
            </v-row>
          </template>
          <template slot-scope="scope" slot="timeCommom">
            <v-row
              no-gutters
              justify="end"
              class="px-2 itemDesc"
              :class="{
                rowIsSelected: checkIfSelected(scope.row),
                rowIsDragged: checkIfSelected(scope.row) && isDragging,
              }"
              @click="handleClickRow(scope.row)"
              @[dragenterEvent]="handleDragEnter($event)"
              @[dragoverEvent]="handleDragOver($event)"
              @[dragleaveEvent]="handleDragLeave($event)"
              @[dropEvent]="handleDrop($event, scope.row)"
            >
              <span v-if="scope.row.name" class="pl-0">
                {{
                  folderData.get(scope.row.id)
                    ? folderData.get(scope.row.id).length
                      ? formatTime(folderData.get(scope.row.id).length)
                      : ''
                    : '...'
                }}
              </span>
              <div v-else-if="!!scope.row.type" />
              <span
                class="text-right"
                v-else-if="
                  scope.row['length'] &&
                  (scope.row.status === 'CONVERTED' ||
                    scope.row.status === 'CONVERTING' ||
                    scope.row.status === 'BLOCKED')
                "
              >
                {{ formatTime(scope.row['length']) }}
              </span>
              <span v-else-if="scope.row.hasOwnProperty('video_source')"
                >-</span
              >
            </v-row>
          </template>
          <template slot-scope="scope" slot="lastModifiedCommom">
            <v-row
              no-gutters
              justify="end"
              class="px-2 itemDesc"
              :class="{
                rowIsSelected: checkIfSelected(scope.row),
                rowIsDragged: checkIfSelected(scope.row) && isDragging,
              }"
              @click="handleClickRow(scope.row)"
              @[dragenterEvent]="handleDragEnter($event)"
              @[dragoverEvent]="handleDragOver($event)"
              @[dragleaveEvent]="handleDragLeave($event)"
              @[dropEvent]="handleDrop($event, scope.row)"
            >
              <div v-if="!!scope.row.type"></div>
              <span class="text-center" v-else>
                {{
                  new Date(scope.row.updated_at).toLocaleString('pt-BR', {
                    day: '2-digit',
                  }) +
                  ' ' +
                  new Date(scope.row.updated_at)
                    .toLocaleString(getLocale(), { month: 'short' })
                    .charAt(0)
                    .toUpperCase() +
                  new Date(scope.row.updated_at)
                    .toLocaleString(getLocale(), { month: 'short' })
                    .slice(1) +
                  ' ' +
                  new Date(scope.row.updated_at).toLocaleString('pt-BR', {
                    year: 'numeric',
                  })
                }}
              </span>
            </v-row>
          </template>
        </vue-virtual-table>

        <!-- empty list -->
        <template v-else>
          <v-container
            :data-intro="
              !selectedItems.length
                ? $t('message.introJS.videos.listEmpty')
                : null
            "
            class="empty__container"
            fill-height
          >
            <v-row
              class="d-flex flex-column"
              no-gutters
              justify="center"
              align="center"
            >
              <img
                class="emptyStateTitle mb-4"
                src="@/assets/videosEmptyState.svg"
                max-width="240"
              />
              <span class="noFilesAddedText emptyStateTitle textType3--text">
                {{ $t('message.noFilesInFolder') }}</span
              >
              <span class="noFilesAddedText emptyStateDesc textType6--text">
                {{ $t('message.noFilesInFolderDesc') }}</span
              >
              <v-btn
                class="btnStyle elevation-0 mt-4"
                :color="$vuetify.theme.dark ? 'btnColor' : 'primary'"
                small
                @click="
                  !isBeta && !isDotCom
                    ? handleImportModal()
                    : $root.$emit('openCreateVideoModal')
                "
              >
                <span> {{ $t('message.importVideos') }} </span>
              </v-btn>
            </v-row>
          </v-container>
        </template>
      </drop-area>

      <!-- mouse select effect -->
      <div
        id="mousearea__effect"
        v-show="mArea.isSelecting && !isDragging"
        :style="mArea.style"
        class="mousearea__effect"
      />
      <div
        v-if="!mArea.isSelecting && isDragging"
        :class="{
          cascate: movedItems.length > 1,
          'dark-theme': $vuetify.theme.dark,
        }"
        class="mousearea__dragging"
      >
        <span
          v-if="movedItems[0]"
          class="mousearea__dragging--name d-flex align-center"
        >
          <!-- <v-icon style="margin-right: 5px" color="turquisePanda">
            {{ movedItems[0].name ? 'mdi-folder' : 'mdi-play-circle-outline' }} test
          </v-icon> -->
          <span class="mr-2 mt-2 align-center"
            ><simple-svg :src="require('@/assets/playBoxOutline.svg')"
          /></span>
          <span>{{ movedItems[0].name || movedItems[0].title }}</span>
        </span>
        <span v-if="movedItems.length > 1" class="mousearea__dragging--counter">
          {{ movedItems.length }}
        </span>
      </div>

      <v-menu
        v-model="showPreview"
        max-width="180px"
        :position-x="x"
        :position-y="y"
        absolute
        offset-y
        :transition="null"
        content-class="elevation-0"
      >
        <v-row
          class="d-flex flex-column"
          no-gutters
          justify="center"
          align="center"
        >
          <img
            :src="selectedPreview"
            onload="this.style='display: block'"
            onerror="this.style='display: none'"
            max-width="180"
          />
        </v-row>
      </v-menu>

      <VideoContextMenu
        :menuItem="menuItem"
        :selectedItems="selectedItems"
        :downloadServer="downloadServer"
        :drmGroupsLoading="drmGroupsLoading"
        :isDrmActive="isDrmActive"
        :drmGroups="drmGroups"
        :disableVideoCopyLinks="disableVideoCopyLinks"
        :xMenu="xMenu"
        :yMenu="yMenu"
        :screenHeight="screenHeight"
        :showMenu="showMenu"
        @setDownloadServer="(data) => (downloadServer = data)"
        @setShowMenu="(data) => (showMenu = data)"
      />
      <!-- folders-tree menu -->
      <v-menu
        v-model="openFolderTree"
        min-width="300px"
        absolute
        offset-y
        :position-x="menuPosition.x"
        :position-y="menuPosition.y"
        :transition="null"
      >
        <v-list dense>
          <!-- root -->
          <v-list-item @click="handleRoot">
            <v-list-item-icon>
              <v-icon>mdi-folder</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              <span>{{ $t('message.videos') }}</span>
            </v-list-item-title>
          </v-list-item>

          <!-- folders -->
          <template v-for="(id, index) in foldersTree.ids">
            <v-list-item
              :key="index"
              :to="{ path: foldersTree.collection.get(id).path }"
            >
              <v-list-item-icon>
                <v-icon>mdi-folder</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                <span>{{ foldersTree.collection.get(id).name }}</span>
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>

      <!-- dialogs -->
      <RecapModal
        :openRecapModal="openRecapModal"
        @closeRecapModal="openRecapModal = false"
      ></RecapModal>
      <drm-add-group-modal />
      <folder-modal />
      <video-modal-rename />
      <video-delete-confirm />
      <move-modal />
      <upload-error-modal />
      <drm-modal-dashboard />
      <add-external-source-video-modal
        :open="openAddExternalVideoModal"
        :currentFolderId="currentFolderId"
        @close="openAddExternalVideoModal = false"
      />
      <v-dialog v-model="showMenuEmbed" width="600px">
        <embed-video
          v-if="mustShowEmbedModal"
          :openFromVideoList="true"
          :videoId="videoId"
          :showMenuEmbed="showMenuEmbed"
          :startTime="startTime"
          :timeVideo="timeVideo"
          :embedSelectedProp="embedSelected"
          :fetching="fetching"
          :video="video"
          :isExternalSourceVideo="isExternalSourceVideo"
          @start-time="startTime = $event"
          @showEmbed="(val) => (showMenuEmbed = val)"
        />
      </v-dialog>
      <duplicate-video-modal
        :openModal="openDuplicateVideoModal"
        :videoId="videoToBeDuplicated ? videoToBeDuplicated : ''"
        @close="openDuplicateVideoModal = false"
      />
      <v-dialog
        v-model="moveConfirmation"
        width="550px"
        :persistent="isRelocating"
      >
        <v-card dense>
          <v-toolbar elevation="0">
            <v-toolbar-title
              class="text--subtitle primary--text"
              style="padding-left: 10px"
            >
              <v-icon color="primary" style="padding-bottom: 2px">
                mdi-play-box-multiple-outline
              </v-icon>
              <span>{{ $t('message.movingItems.title') }}</span>
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn v-if="!isRelocating" icon @click="handleClose">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text
            class="px-7 py-0"
            v-html="
              $t('message.movingItems.message', {
                count: movedItems.length,
                folder: selectedFolder.name,
              })
            "
          />
          <v-card-actions class="d-flex justify-end px-7 pb-6">
            <base-btn
              :disabled="isRelocating"
              class="mr-3"
              type="secondary"
              @click="handleClose"
            >
              {{ $t('message.cancel') }}
            </base-btn>
            <base-btn :loading="isRelocating" @click="confirmMove">
              {{ $t('message.confirm') }}
            </base-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- actions -->
      <v-row class="data-table__actions d-flex" no-gutters>
        <div>
          <v-col
            v-if="hasEditVideoPerm"
            v-show="showFloatMenu"
            :cols="isMobileBreakpoint || isTabletBreakpoint ? 12 : 'auto'"
          >
            <base-float-menu
              :minWidth="
                isMobileBreakpoint || isTabletBreakpoint ? '280px' : '710px'
              "
            >
              <span class="selectedItensText textType3--text">
                {{ selectedItems.length }}
                {{ $t('message.selectedItems') }}</span
              >
              <div style="border-left: 1px solid #d1d1d1">
                <span v-if="isMobileBreakpoint || isTabletBreakpoint">
                  <!-- Botão para abrir o modal -->
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <base-btn
                        class="ml-4"
                        size="small"
                        :disabled="!selectedItems.length || !canSeePlanInfo"
                        v-bind="attrs"
                        v-on="on"
                        @click="showActionsModal = true"
                      >
                        {{ $t('message.floatActions') }}
                      </base-btn>
                    </template>
                  </v-menu>

                  <v-dialog v-model="showActionsModal" fullscreen persistent>
                    <v-card>
                      <v-toolbar dense flat>
                        <v-toolbar-title class="ml-2">{{
                          $t('message.floatActions')
                        }}</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn icon @click="showActionsModal = false">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-toolbar>

                      <v-card-text>
                        <p>
                          {{ selectedItems.length }}
                          {{ $t('message.selectedItems') }}
                        </p>
                        <v-list>
                          <v-list-item
                            link
                            @click="handleOpenSubModal"
                            class="pa-0 pl-5"
                          >
                            <v-row
                              no-gutters
                              class="align-center d-flex flex-row flex-nowrap pa-0 justify-space-between"
                              justify="start"
                            >
                              <v-col cols="auto" class="pa-0">
                                <span>{{
                                  $t('message.ai.subtitles.generateSubtitle')
                                }}</span>
                              </v-col>
                              <v-col cols="2" class="py-0 mr-2 ml-5">
                                <simple-svg
                                  :src="require('@/assets/IconAI.svg')"
                                />
                              </v-col>
                            </v-row>
                          </v-list-item>

                          <!-- Gerar Dublagem -->
                          <v-list-item
                            link
                            @click="handleOpenDubModal"
                            class="pa-0 pl-5"
                          >
                            <v-row
                              no-gutters
                              class="align-center d-flex flex-row flex-nowrap pa-0 justify-space-between"
                              justify="start"
                            >
                              <v-col cols="auto" class="pa-0">
                                <span>{{
                                  $t('message.ai.dubbings.generateDubbing')
                                }}</span>
                              </v-col>
                              <v-col cols="2" class="py-0 mr-2 ml-1">
                                <simple-svg
                                  :src="require('@/assets/IconAI.svg')"
                                />
                              </v-col>
                            </v-row>
                          </v-list-item>

                          <v-divider></v-divider>

                          <!-- Mover Arquivos -->
                          <v-list-item>
                            <span
                              :disabled="!selectedItems.length"
                              @click="
                                handleMoveFiles()
                                showActionsModal = false
                              "
                            >
                              {{ $t('message.move') }}
                            </span>
                          </v-list-item>

                          <!-- Duplicar Vídeo -->
                          <v-list-item v-if="!isExternalSourceVideo">
                            <span
                              :disabled="!justOneConvertedItemSelected"
                              @click="
                                openDuplicateVideoModal = true
                                showActionsModal = false
                              "
                            >
                              {{ $t('message.duplicate') }}
                            </span>
                          </v-list-item>

                          <!-- Download CSV -->
                          <v-list-item>
                            <span
                              :disabled="
                                !selectedItems.length ||
                                !isLoadedPlanInfo ||
                                selectedItems.length === 0
                              "
                              @click="downloadCSV"
                            >
                              Download CSV
                            </span>
                          </v-list-item>

                          <v-divider></v-divider>
                          <!-- Remover Item -->
                          <v-list-item>
                            <span
                              style="height: 100%; color: #f44336"
                              :loading="isLoading"
                              :disabled="isButtonDisabled"
                              @click="removeModal"
                            >
                              {{ $t('message.remove') }}
                            </span>
                          </v-list-item>
                        </v-list>
                      </v-card-text>
                    </v-card>
                  </v-dialog>
                </span>
                <template v-else>
                  <v-menu offset-y top transition="slide-y-reverse-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <base-btn
                        class="ml-4"
                        size="small"
                        :disabled="!selectedItems.length || !canSeePlanInfo"
                        v-bind="attrs"
                        v-on="on"
                        @click="handleSelectedItems"
                      >
                        {{
                          $t('message.pages.video.playerOptionTitles.pandaIa')
                        }}
                        <template>
                          <v-icon>mdi-chevron-down</v-icon>
                        </template>
                      </base-btn>
                    </template>

                    <v-list>
                      <v-list-item link @click="subModal" class="pa-0 pl-5">
                        <v-row
                          no-gutters
                          class="align-center d-flex flex-row flex-nowrap pa-0 justify-space-between"
                          justify="start"
                        >
                          <v-col cols="auto" class="pa-0">
                            <v-list-item-content
                              >{{
                                $t('message.ai.subtitles.generateSubtitle')
                              }}...</v-list-item-content
                            >
                          </v-col>
                          <v-col cols="2" class="py-0 mr-2 ml-5">
                            <v-icon color="#3F55EE"> panda-ai </v-icon>
                          </v-col>
                        </v-row>
                      </v-list-item>
                      <!-- dub -->
                      <v-list-item link @click="dubModal" class="pa-0 pl-5">
                        <v-row
                          no-gutters
                          class="align-center d-flex flex-row flex-nowrap pa-0 justify-space-between"
                          justify="start"
                        >
                          <v-col cols="auto" class="pa-0">
                            <v-list-item-content
                              >{{
                                $t('message.ai.dubbings.generateDubbing')
                              }}...</v-list-item-content
                            >
                          </v-col>
                          <v-col cols="2" class="py-0 mr-2 ml-1">
                            <v-icon color="#3F55EE"> panda-ai </v-icon>
                          </v-col>
                        </v-row>
                        <!--  -->
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <!-- Botões e itens de menu -->
                  <base-btn
                    class="ml-2"
                    size="small"
                    type="neutral"
                    :disabled="!selectedItems.length"
                    @click="moveModal"
                  >
                    {{ $t('message.move') }}
                  </base-btn>
                  <base-btn
                    type="neutral"
                    size="small"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="!justOneConvertedItemSelected"
                    @click="duplicateModal"
                    v-if="
                      !isExternalSourceVideo && justOneConvertedItemSelected
                    "
                  >
                    {{ $t('message.duplicate') }}
                  </base-btn>
                  <v-tooltip
                    v-if="!justOneConvertedItemSelected"
                    top
                    content-class="dupTooltip"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <base-btn
                        type="neutral"
                        size="small"
                        v-bind="attrs"
                        v-on="on"
                        :disabled="!justOneConvertedItemSelected"
                        @click="duplicateModal"
                        v-if="!isExternalSourceVideo"
                      >
                        {{ $t('message.duplicate') }}
                      </base-btn>
                    </template>
                    <span>
                      {{ $t('message.dupVideosWarning') }}
                    </span>
                  </v-tooltip>
                  <base-btn
                    type="neutral"
                    size="small"
                    :disabled="
                      !selectedItems.length ||
                      !isLoadedPlanInfo ||
                      selectedItems.length === 0
                    "
                    @click="downloadCSV"
                  >
                    Download CSV
                  </base-btn>
                  <base-btn
                    size="small"
                    type="danger"
                    :loading="isLoading"
                    :disabled="isButtonDisabled"
                    @click="removeModal"
                  >
                    {{ $t('message.remove') }}
                  </base-btn>
                </template>
              </div>
            </base-float-menu>
          </v-col>
        </div>
      </v-row>
      <download-alert-dialog />
    </template>
    <noscript
      ><iframe
        src="https://www.googletagmanager.com/ns.html?id=GTM-KXCFR43"
        height="0"
        width="0"
        style="display: none; visibility: hidden"
      ></iframe
    ></noscript>
  </page-view>
</template>

<script>
// external imports
import VueVirtualTable from 'vue-virtual-table'
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import prettyBytes from 'pretty-bytes'
import Dexie from 'dexie'
import _ from 'lodash'

// internal imports
import calcMegabytesMixin from '@/utils/calcMegabytes'
import externalSourceVideo from '@/utils/externalSourceVideo.mixin.js'
import videosListMixin from '@/utils/videosList.mixin.js'
import { embedOptions as platformsOfEmbed } from '@/utils/embedOptions.js'
import i18n from '@/translations/lang'
import Storage from '@/utils/Storage'
import { PANDA_STATUS_PAGE, PANDA_HELP_CONVERTING } from '@/config/envs'
import { BACKUP_DOWNLOADER_SERVER } from '@/config/envs'
import embedCode from '@/utils/embedCode.mixin.js'
import embedOptionOrientations from '@/utils/embedOptionOrientations.js'
import introMixin from '@/utils/intro.mixin.js'
import formatTimeMixin from '@/utils/formatTime.mixins.js'
import downloadUrlMixin from '@/utils/downloadUrl.mixin.js'
import langCodesMixin from '@/utils/langCodes.mixin.js'
import batchAiGenerationMixin from '@/utils/batchAiGeneration.mixin.js'

// API
import videosApi from '@/services/videosApi'
import foldersApi from '@/services/foldersApi'
import hostsApi from '@/services/hostsApi'

// Components
import DropArea from './NewVideo/DropArea.vue'
import DrmAddGroupModal from '@/components/DrmAddGroupModal.vue'
import BaseBtn from '@/components/BaseBtn.vue'
import FolderModal from '@/components/FolderModal.vue'
import VideoModalRename from '@/components/VideoModalRename.vue'
import VideoDeleteConfirm from './VideoDeleteConfirm.vue'
import MoveModal from '@/components/MoveModal.vue'
import DuplicateVideoModal from '@/components/DuplicateVideoModal.vue'
import UploadErrorModal from '@/components/UploadErrorModal.vue'
import DrmModalDashboard from '@/components/DrmModalDashboard.vue'
import BaseFloatMenu from '@/components/BaseFloatMenu.vue'
import EmbedVideo from '@/components/EmbedVideo.vue'
import PageView from '@/layout/PageViewV2.vue'
import AddExternalSourceVideoModal from '@/components/AddExternalSourceVideoModal'
import SearchBar from '@/components/SearchBar.vue'
import DownloadAlertDialog from '@/components/DownloadAlertDialog.vue'
import VideoContextMenu from '@/components/VideoContextMenu.vue'
import FlagTooltip from '@/components/FlagTooltip.vue'
import AiBanner from '@/components/AiBanner.vue'
import TrainingBanner from '@/components/TrainingBanner.vue'
import RecapBanner from '@/components/Recap/MainPageRecapBanner.vue'
import RecapModal from '@/components/Recap/RecapModal.vue'
import BlackFridayBanner from '@/components/BlackFridayBanner.vue'
import FlagCards from '@/components/FlagCards.vue'

const newGhostImg = document.createElement('img')
newGhostImg.setAttribute(
  'src',
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQIW2OYKJ38HwAEXwIP2K67AAAAAABJRU5ErkJggg=='
)
newGhostImg.style =
  'position:absolute;top:0;left:0;z-index:-1;width:0.1px;height:0.1px;'
newGhostImg.classList.add('hideGhostImage')

export default {
  name: 'Videos',
  components: {
    SearchBar,
    EmbedVideo,
    BaseFloatMenu,
    VideoContextMenu,
    DropArea,
    VueVirtualTable,
    DrmAddGroupModal,
    FolderModal,
    VideoModalRename,
    VideoDeleteConfirm,
    MoveModal,
    UploadErrorModal,
    DrmModalDashboard,
    DuplicateVideoModal,
    PageView,
    BaseHeader: () => import('@/components/BaseHeaderV2.vue'),
    BaseBtn,
    AddExternalSourceVideoModal,
    DownloadAlertDialog,
    FlagTooltip,
    FlagCards,
    AiBanner,
    TrainingBanner,
    RecapBanner,
    RecapModal,
    BlackFridayBanner,
  },
  data: () => ({
    showAiBanner: false,
    showTrainingBanner: false,
    showRecapBanner: false,
    showBlackFridayBanner: false,
    bannersExpiredDelay: false,
    scrollThreshold: null,
    showActionsModal: false,
    tableHeaderVisible: true,
    menuVisible: false,
    menuX: 0,
    menuY: 0,
    columns: [
      {
        name: i18n.t('message.ai.originalLang'),
        visible: true,
      },
      { name: i18n.t('message.pages.video.subtitle'), visible: true },
      { name: i18n.t('message.ai.dubbings.dubbing'), visible: true },
      {
        name: i18n.t('message.pages.videosDataTable.header.size'),
        visible: true,
      },
      {
        name: i18n.t('message.pages.videosDataTable.header.time'),
        visible: true,
      },
      {
        name: i18n.t('message.pages.videosDataTable.header.lastModification'),
        visible: true,
      },
      { name: 'DRM', visible: true },
    ],
    settedOrder: '',
    isVideo: false,
    showFloatMenu: true,
    isLoading: false,
    isRenameModalOpen: false,
    isDotCom: false,
    searchFuseOptions: {
      keys: ['title', 'name', 'id', 'video_external_id', 'description'],
      includeScore: true,
      threshold: 0.4,
    },
    filterList: 'all',
    isExternalSourceVideo: false,
    openDuplicateVideoModal: false,
    videoToBeDuplicated: undefined,
    showMenuEmbed: false,
    mustShowEmbedModal: false,
    videoId: '',
    video: {},
    menuPosition: {
      x: 1920,
      y: 72,
    },
    embedVideo: {
      width: 720,
      height: 360,
      startTimeCheckbox: false,
      startTime: '',
    },
    videoCustomSize: false,
    embedSelected: '',
    timeVideo: 0,
    tooltipVisible: false,
    fetching: true,
    startTime: '00:00:00',
    radioActive: 0,
    nameOrder: 'asc',
    sizeOrder: 'asc',
    timeOrder: 'asc',
    lastModifiedOrder: 'asc',
    langOrder: 'asc',
    tableHeight: window.innerHeight - 300,
    authToken: '',
    showMenu: false,
    folderVideosCount: {},
    blockFolder: false,
    showPreview: false,
    previewTimeout: false,
    clearPreviewTimeout: false,
    selectedPreview: false,
    menuItem: {},
    x: 0,
    y: 0,
    xMenu: 0,
    yMenu: 0,
    isHandleImportModal: true,
    isLoadedPlanInfo: false,
    isFetching: false,
    selectedItems: [],
    filesLoading: false,
    embedOptionOrientations,
    error: {
      getFilesError: false,
      shownErrMsg: '',
      tooltipErrMsg: '',
    },
    listQuery: {
      root_folder: 1,
      page: 1,
      limit: 5000,
      status: undefined,
      title: undefined,
      folder_id: null,
    },
    checkVideosInterval: null,
    videoIdsGetStatus: {},
    videosStatus: {
      converted: 0,
      converting: 0,
      draft: 0,
      deleting: 0,
      failed: 0,
    },
    unknownErrorCode: '504',
    isFolder: false,
    folder: {
      name: '',
      updated_at: '',
      created_at: '',
      videos: [],
      id: '',
    },
    lastFolder: {
      name: '',
      updated_at: '',
      created_at: '',
      parent_folder_id: '',
      videos: [],
      id: '',
    },
    resizeWindowTimeout: null,
    embedTrademark:
      Storage.getItem('embedType') ??
      i18n.t('message.pages.videoEditModal.embedConfiguration.default'),
    loadedVideos: [],
    loadedFolders: [],
    db: null,
    dbVideosLength: 0,
    disableVideoCopyLinks: false,
    csvItems: [],
    csvLoading: false,
    downloadServer: '',
    isBeta: false,
    embedCode: {
      useMenuItem: true,
    },
    hlsEmbed: false,
    codeEmbed: false,
    srcEmbed: false,
    externalIdEmbed: false,
    dbFailed: false,
    deletedCount: 0,
    videosLimit: 3000,
    filter: '',
    allDeletedItems: [],
    openAddExternalVideoModal: false,
    selectedFolder: {},
    movedItems: [],
    oldFolderId: '',
    availableSpaceBelow: 0,
    screenHeight: 0,
    moveConfirmation: false,
    isRelocating: false,
    mArea: {
      isDragging: false,
      isSelecting: false,
      isMouseDown: false,
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 0,
      startTime: null,
      isPosX: null,
      isPosY: null,
      style: 'display:none;width:0.1px;height:0.1px;top:0;left:0;opacity:0',
      thresholdTime: 1,
      thresholdDistance: 5,
      timeoutDown: null,
      timeoutMove: null,
      timeoutDrag: null,
      firstDragElem: false,
    },
    keySelect: {
      selectedIndex: null,
      midIndex: null,
      arrowKey: 'ArrowDown',
      isShiftKeyPressed: false,
      isCtrlKeyPressed: false,
      lastIdxSelected: false,
      lastShiftIdxSelected: false,
      isAnyOtherModal: false,
    },
    folderData: new Map(),
    folderItemsQuantityData: new Map(),
    bypassClickCloseMenu: false,
    qualityToBeDownloaded: null,
    foldersTree: {
      threshold: 2,
      ids: new Set(),
      collection: new Map(),
    },
    openFolderTree: false,
    isUpdatingVisibility: false,
    svgContent: null,
    openRecapModal: null,
  }),
  mixins: [
    calcMegabytesMixin,
    embedCode,
    introMixin,
    externalSourceVideo,
    videosListMixin,
    formatTimeMixin,
    downloadUrlMixin,
    langCodesMixin,
    batchAiGenerationMixin,
  ],
  computed: {
    ...mapGetters('user', ['canSeePlanInfo']),
    ...mapState('user', {
      planInfo: 'userPlanInfo',
      mainVideos: 'mainVideos',
      subFolderVideos: 'subFolderVideos',
      deletedFolders: 'deletedFolders',
      isTabletBreakpoint: 'isTabletBreakpoint',
      isMobileBreakpoint: 'isMobileBreakpoint',
      userFirstAccess: 'userFirstAccess',
      isRecapAvailable: 'isRecapAvailable',
      isDrmActive: (state) => state.drmData.isDrmActive,
      drmGroupsLoading: (state) => state.drmData.loadings.drmGroups,
      drmGroups: (state) => state.drmData.drmGroups,
    }),
    shouldShowFloatMenu() {
      return this.selectedItems.length > 0
    },
    contentHeight() {
      let additionalHeight = this.allItems.length > 0 ? 400 : 900
      let contentHeight = this.allItems.length * 48 + additionalHeight

      return Math.max(contentHeight, 900)
    },
    isButtonDisabled() {
      return (
        !this.selectedItems.length ||
        this.hasSomeDisabledVideo ||
        this.blockFolder ||
        this.isLoading
      )
    },
    tableConfig() {
      const tableConfig = [
        {
          prop: '_action',
          name: 'action',
          actionName: 'selectCommon',
          width: 50,
          alignItems: 'start',
        },
        {
          prop: '_action',
          name: i18n.t('message.name'),
          actionName: 'titleCommon',
          alignItems: 'start',
        },
        {
          prop: '_action',
          name: i18n.t('message.size'),
          actionName: 'langCommon',
          width: 96,
          sortable: true,
          isHidden: !this.columns[0].visible,
          // beta: true,
          alignItems: 'center',
        },
        {
          prop: '_action',
          name: i18n.t('message.size'),
          actionName: 'subCommon',
          width: 96,
          sortable: true,
          isHidden: !this.columns[1].visible,
          // beta: true,
          alignItems: 'center',
        },
        {
          prop: '_action',
          name: i18n.t('message.size'),
          actionName: 'dubCommon',
          width: 96,
          sortable: true,
          isHidden: !this.columns[2].visible,
          // beta: true,
          alignItems: 'center',
        },
        {
          prop: '_action',
          name: i18n.t('message.size'),
          actionName: 'sizeCommom',
          sortable: true,
          width: 96,
          isHidden: !this.columns[3].visible,
          alignItems: 'center',
        },
        {
          prop: '_action',
          actionName: 'timeCommom',
          name: i18n.t('message.time'),
          width: 96,
          isHidden: !this.columns[4].visible,
          alignItems: 'center',
        },
        {
          prop: '_action',
          actionName: 'lastModifiedCommom',
          name: i18n.t('message.lastModification'),
          isHidden: !this.columns[5].visible,
          sortable: true,
          width: 114,
          alignItems: 'center',
        },
        // dotsColumn
        {
          prop: '_action',
          name: 'DRM',
          actionName: 'drmColumn',
          isHidden: !this.columns[6].visible,
          width: 40,
          alignItems: 'start',
        },
        {
          prop: '_action',
          name: 'DRM',
          actionName: 'drmColumnSpacer',
          isHidden: this.columns[6].visible,
          width: 40,
          alignItems: 'start',
        },
        {
          prop: '_action',
          actionName: 'dotsColumn',
          name: '',
          width: 50,
          alignItems: 'center',
        },
      ]

      return tableConfig
    },
    searchList() {
      let folders = [...this.mainFolders]
      // eslint-disable-next-line no-unused-vars
      let vids = this.allItems
      const _this = this
      if (this.filterList === 'all' || this.filterList === 'videos') {
        //eslint-disable-next-line vue/no-async-in-computed-properties
        return new Promise((resolve) => {
          window.db.videos.toArray().then((list) => {
            if (_this.filterList === 'all') {
              for (const folder of [...folders]) {
                list.push(folder)
              }
            }
            return resolve(list)
          })
        })
      } else if (this.filterList === 'folders') {
        return [...folders]
      }
      return []
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    permissions() {
      if (this.planInfo?.subscription_metadata?.blocked_permissions) {
        try {
          const data = JSON.parse(
            this.planInfo?.subscription_metadata?.blocked_permissions
          )
          if (Array.isArray(data)) return data
        } catch (err) {
          console.error(err)
        }
      }
      return []
    },
    currentFolderName() {
      if (this.folder?.name) {
        return this.folder.name
      }
      return ''
    },
    currentFolderId() {
      if (this.folder?.id) {
        return this.folder.id
      }
      return false
    },
    checkIfSubscriptionIsActive() {
      if (!this.planInfo?.subscription) return false
      if (!this.planInfo?.subscription?.active) return false
      return true
    },
    getDefaultColor() {
      return this.$vuetify.theme.dark ? 'main' : 'white'
    },
    filteredVids() {
      return this.loadedVideos.filter((video) => video.status != 'DELETING')
    },
    allItems() {
      this.handleSettedOrder()
      return this.loadedFolders.concat(this.filteredVids)
    },
    thisFolderFolders() {
      return this.mainFolders.filter((folder) => {
        return folder.parent_folder_id === this.$route.params.id
      })
    },
    insideFolderVideos() {
      return this.subFolderVideos
    },
    rootFolders() {
      return this.mainFolders.filter((folder) => {
        return folder.parent_folder_id === null
      })
    },
    anyItemSelected() {
      return this.selectedItems.length > 0
    },
    hasSomeDisabledVideo() {
      if (
        this.selectedItems.length &&
        this.selectedItems.some((item) => item.status === 'BLOCKED')
      ) {
        return true
      }
      return false
    },
    justOneConvertedItemSelected() {
      if (
        this.selectedItems.length === 1 &&
        this.selectedItems[0].status === 'CONVERTED'
      ) {
        return true
      }
      return false
    },
    checkboxIndeterminateStatus() {
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.length < this.allItems.length
      )
    },
    inRoute() {
      return this.$route
    },
    aspectRatio() {
      const { width, height } = this.menuItem
      let ratio = height / width
      if (Number.isNaN(ratio)) {
        ratio = 9 / 16
      }
      return `${ratio * 100}%`
    },
    isLandscapeOrientation() {
      return parseFloat(this.aspectRatio) < 100
    },
    embedOptions() {
      const [general] = platformsOfEmbed.filter(
        ({ value }) => value === 'general'
      )
      const rest = platformsOfEmbed.filter(({ value }) => value !== 'general')
      const sorted = rest.sort((a, b) => a.value.localeCompare(b.value))
      return [general, ...sorted]
    },
    pageTitle() {
      return this.isFolder
        ? this.lastFolder?.name
          ? this.lastFolder?.name
          : this.$i18next.t('message.videos')
        : this.$i18next.t('message.videos')
    },
    pageSubtitle() {
      return this.isFolder
        ? this.folder?.name
          ? this.folder?.name
          : this.lastFolder?.name
        : ''
    },
    pageMenu() {
      const i18n = this.$i18next
      const path = 'message.pages.video.pageMenu'
      const currentPath = this.$route.path

      return [
        {
          title: i18n.t(`${path}.videos`),
          to: currentPath.startsWith('/folders') ? currentPath : '/videos',
          exact: false,
        },
        {
          title: i18n.t(`${path}.trash`),
          to: '/video/trash',
          exact: true,
        },
      ]
    },
    pageInfo() {
      const i18n = this.$i18next
      const path =
        this.allItems.length === 1 || this.dbVideosLength === 1
          ? 'message.pages.video.pageInfoSingular'
          : 'message.pages.video.pageInfoPlural'

      if (this.$route.name !== 'Videos') {
        return i18n.t(path, { count: this.allItems.length })
      }

      return this.dbVideosLength > 0
        ? i18n.t(path, { count: this.dbVideosLength })
        : ''
    },
    isDragging() {
      return this.mArea.isDragging && this.mArea.isMouseDown
    },
    dragstartEvent() {
      return this.mArea.isDragging ? 'dragstart' : null
    },
    dragenterEvent() {
      return this.mArea.isDragging ? 'dragenter' : null
    },
    dragoverEvent() {
      return this.mArea.isDragging ? 'dragover' : null
    },
    dragleaveEvent() {
      return this.mArea.isDragging ? 'dragleave' : null
    },
    dropEvent() {
      return this.mArea.isDragging ? 'drop' : null
    },
    mousedownEvent() {
      return this.mArea.isDragging ? null : 'mousedown'
    },
    mousemoveEvent() {
      return this.mArea.isMouseDown ? 'mousemove' : null
    },
    mouseupEvent() {
      return this.mArea.isMouseDown ? 'mouseup' : null
    },
    mouseleaveEvent() {
      return this.mArea.isMouseDown ? 'mouseleave' : null
    },
    isPtBr() {
      if (this.$i18next.language === 'pt' || this.$i18next.language === 'pt-BR')
        return true
      return false
    },
    isEs() {
      if (this.$i18next.language === 'es') return true
      return false
    },
    isEn() {
      if (this.$i18next.language === 'en') return true
      return false
    },
    isPandaUs() {
      const hostname = window.location.hostname

      if (hostname === 'localhost') {
        return false
      }

      return hostname.endsWith('.com') && !hostname.endsWith('.com.br')
    },
    svgDataUrl() {
      return `data:image/svg+xml;utf8,${encodeURIComponent(this.svgContent)}`
    },
    displayBanner() {
      return (
        this.showRecapBanner ||
        this.showTrainingBanner ||
        this.showAiBanner ||
        this.showBlackFridayBanner
      )
    },
  },
  async created() {
    this.$route.name === 'Videos'
      ? (this.isFolder = false)
      : (this.isFolder = true)
    this.$root.$emit('checkIfHasUploaderUrl')
    this.checkIfHasDownloadUrl()

    let creditsToggle = this.$store.state.user.buyCreditsToggle
    if (creditsToggle) this.$root.$emit('creditsBuyModal')

    await this.pageRequests()
    await this.loadDrmGroups()
    await this.setTrashItems()
    this.setTableHeight()

    window.addEventListener('keydown', this.handleKeyDown)
    window.addEventListener('keyup', this.handleKeyUp)
    try {
      const svgUrl = require('@/assets/dubbing.svg')
      const response = await fetch(svgUrl)
      const svgText = await response.text()
      this.svgContent = svgText
    } catch (error) {
      console.error('Error to loading svg:', error)
    }
  },
  mounted() {
    this.$root.$on('changeLanguage', () => {
      this.updateColumnNames()
    })
    window.addEventListener('contextmenu', () => {
      this.menuX = event.clientX
    })
    this.handleWidth()
    window.addEventListener('resize', this.handleWidth)

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.$root.$on('renameModalOpened', () => {
      this.isRenameModalOpen = true
    })
    this.$root.$on('renameModalClosed', () => {
      this.isRenameModalOpen = false
    })
    this.$root.$on('isAnyOtherModal', (val) => {
      this.keySelect.isAnyOtherModal = val
    })
    this.authToken = Storage.getItem('accessToken')
    this.keySelect.isAnyOtherModal = false
    this.isBeta = location.search.match('beta')
    this.isDotCom = !location.host.includes('.com.br')
    this.$root.$on('updateVideoInListing', (video) =>
      this.updateVideoInArray(video)
    )
    this.embedSelected = ''
    let storageEmbedOption = Storage.getItem('embedType') ?? 'general'
    if (!storageEmbedOption) Storage.setItem('embedType', 'general')
    this.embedSelected = storageEmbedOption.toLowerCase()
    window.addEventListener('resize', this.resizeTimeout)
    clearInterval(this.checkVideosInterval)
    this.$root.$on('moveFilesVideosPage', async (videos) => {
      for (let video of videos) {
        this.loadedVideos = this.loadedVideos.filter(
          (item) => item.id != video.id
        )
        await window.db.videos.update(video.id, video)
      }
      this.selectedItems = []
      await this.confirmMove()
    })
    this.$root.$on('duplicateVideo', (videoId) => {
      this.videoToBeDuplicated = videoId
      this.openDuplicateVideoModal = true
    })
    this.$root.$on('onFinishLoadPlanInfo', () => {
      this.isLoadedPlanInfo = true
    })
    this.$root.$on('onStorageSizeMax', () => {
      this.isLoadedPlanInfo = false
    })
    this.$root.$on('addVideoVideosPage', async (video) => {
      const findIfAlreadyExists = this.loadedVideos.find(
        (searchVideo) => searchVideo.id === video.id
      )
      if (findIfAlreadyExists) return
      const foldersResult = this.mainFolders.map((folder) =>
        folder.id === video.folder_id
          ? {
              ...folder,
              videos_count: folder.videos_count
                ? Number(folder.videos_count) + 1
                : 1,
            }
          : folder
      )
      this.UPDATE_MAIN_FOLDERS(foldersResult)
      this.dbVideosLength += 1
      if (!video.folder_id) {
        video['folder_id'] = 'root'
      }
      this.loadedVideos.unshift(video)
      await window.db.videos.add(video)
    })
    this.$root.$on('updateVideoVideosPage', async (video) => {
      let parentFolderDrmGroups = []
      if (video.folder_id && video.folder_id !== 'root') {
        parentFolderDrmGroups = this.getParentFolderDrmGroups(video.folder_id)
      }
      let updatedVideo = { title: video.title }
      const newNameLoadedVideo = this.loadedVideos.map((item) => {
        if (item.id === video.id) {
          item.title = video.title
          let finalResult = []
          if (video?.drm_group_ids && video?.drm_group_ids.length) {
            finalResult = video.drm_group_ids
          } else if (parentFolderDrmGroups.length) {
            finalResult = parentFolderDrmGroups
          }

          if (finalResult.length) {
            updatedVideo['drm_group_ids'] = finalResult
            item['drm_group_ids'] = finalResult
          } else if (item['drm_group_ids']) {
            delete item['drm_group_ids']
            updatedVideo['drm_group_ids'] = []
          }
        }
        return item
      })
      this.loadedVideos = [...newNameLoadedVideo]
      await window.db.videos.update(video.id, updatedVideo)
      let isParent = this.checkIfPageIsParent(video.parent_folder_id)
      if (isParent) {
        this.loadedVideos = this.loadedFolders.map((item) =>
          item.id == video.id ? video : item
        )
      }
    })
    this.$root.$on('updateVideosPage', (video) => {
      const newSubDubLoadedVideo = this.loadedVideos.map((item) => {
        if (item.id === video.id) {
          item.subtitles = video.subtitles
          item.audios = video.audios
        }
        return item
      })
      this.loadedVideos = newSubDubLoadedVideo
    })
    this.$root.$on('removeVideoVideosPage', async (videos) => {
      this.deletedCount += videos.length
      this.dbVideosLength -= videos.length
      for (let video of videos) {
        const foldersResult = this.mainFolders.map((folder) =>
          folder.id === video.folder_id
            ? {
                ...folder,
                videos_count: folder.videos_count
                  ? Number(folder.videos_count) - 1
                  : 0,
              }
            : folder
        )
        this.UPDATE_MAIN_FOLDERS(foldersResult)
        this.loadedVideos = this.loadedVideos.filter(
          (item) => item.id != video.id
        )
        let updatedVideo = { ...video, status: 'DELETING' }
        await window.db.videos.update(video.id, updatedVideo)
      }
      this.selectedItems = []
    })
    this.$root.$on('addFolderVideosPage', (folder) => {
      let isParent = this.checkIfPageIsParent(folder.parent_folder_id)
      if (isParent) {
        this.loadedFolders.push(folder)
      }
    })
    this.$root.$on('updateFolderVideosPage', async (folder) => {
      let isParent = this.checkIfPageIsParent(folder.parent_folder_id)
      const folderIdsTarget = this.mainFolders
        .filter(({ parent_folder_id }) => parent_folder_id === folder.id)
        ?.map((item) => item.id)
      folderIdsTarget.push(folder.id)
      let targetVideos = await window.db.videos
        .where('folder_id')
        .anyOf(folderIdsTarget)
        .toArray()
      targetVideos = targetVideos.map((item) => {
        if (folder.drm_group_ids) {
          item.drm_group_ids = folder.drm_group_ids
        } else {
          item.drm_group_ids = []
        }
        return item
      })
      targetVideos.forEach(async (item) => {
        await window.db.videos.update(item.id, {
          drm_group_ids: item.drm_group_ids,
        })
      })
      if (isParent) {
        this.loadedFolders = this.loadedFolders.map((item) =>
          item.id == folder.id ? folder : item
        )
      }
      await this.readUserFolders()
      if (this.$route.params.id) {
        this.loadedFolders = this.mainFolders.filter((folder) => {
          return folder.parent_folder_id === this.$route.params.id
        })
      } else {
        this.loadedFolders = this.rootFolders
      }
      this.$root.$emit('finishUpdateFolderVideosPage')
    })
    this.$root.$on('removeFolderVideosPage', (folder) => {
      this.deletedCount += 1
      this.loadedFolders = this.loadedFolders.filter(
        (item) => item.id != folder.id
      )
      this.selectedItems = []
    })
    this.$root.$on('resetIsAnyOtherModal', (value = false) => {
      this.keySelect.isAnyOtherModal = value
    })
    if (this.planInfo?.limit) {
      this.isLoadedPlanInfo = true
    }
    this.$root.$emit('readUploadServers')
    this.setEmbed(
      Storage.getItem('embedType') ??
        i18n.t('message.pages.videoEditModal.embedConfiguration.default')
    )
    this.$nextTick(() => {
      this.setTableHeight()
    })
    const splittedHref = window.location.href.split('?')
    const params = this.paramsToObject(
      new URLSearchParams(splittedHref[splittedHref.length - 1])
    )
    this.handleEmailConfirmChange(params)

    const conditionShowAiCtaModal = Storage.getItem('hasSeenAiCtaModal')

    if (!conditionShowAiCtaModal) {
      this.$root.$emit('openAiCtaModal')
    }
    this.loadColumnsVisibility()
  },
  beforeDestroy() {
    clearInterval(this.checkVideosInterval)
    this.$root.$off('onFinishLoadPlanInfo')
    this.$root.$off('onStorageSizeMax')
    this.$root.$off('moveFilesVideosPage')
    this.$root.$off('updateVideoVideosPage')
    this.$root.$off('removeVideoVideosPage')
    this.$root.$off('addFolderVideosPage')
    this.$root.$off('updateFolderVideosPage')
    this.$root.$off('removeFolderVideosPage')
    this.$root.$off('resetIsAnyOtherModal')
    this.$root.$off('showAccountWarning')
    this.$root.$off('renameModalOpened')
    this.$root.$off('renameModalClosed')
    this.$root.$off('AiCtaModalHasClosed')
    this.$root.$off('duplicateVideo')

    window.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('contextmenu', () => {
      this.menuX = event.clientX
    })
    window.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('resize', this.handleWidth)
    window.removeEventListener('keyup', this.handleKeyUp)
    window.removeEventListener('resize', this.resizeTimeout)
  },
  watch: {
    isRecapAvailable: {
      handler(isRecapAvailable) {
        this.handleBanners(isRecapAvailable)
      },
    },
    selectedItems: {
      handler() {
        this.showFloatMenu = this.shouldShowFloatMenu
      },
      immediate: true,
    },
    folderItemsQuantityData: {
      handler(newVal) {
        newVal.forEach((value, id) => {
          if (value === '(NaN)') {
            this.newFolderCount(id)
          }
        })
      },
      immediate: true,
      deep: true,
    },
    showMenu: function () {
      this.$root.$emit('changeEmbed')
      let embedStored = Storage.getItem('embedType')
        ? Storage.getItem('embedType')
        : 'general'

      if (Storage.getItem('embedType')) {
        let embedOptionTrademark = this.embedOptions.map((val) => val.value)
        this.embedTrademark =
          this.embedOptions[embedOptionTrademark.indexOf(embedStored)].text
      } else {
        this.embedTrademark = embedStored
      }
    },
    loadedVideos: function () {
      this.checkVideosInterval && clearInterval(this.checkVideosInterval)
      this.checkVideosInterval = setInterval(() => {
        this.statusCheck()
        this.setTableHeight()
      }, 10000)
    },
    inRoute: function () {
      this.checkAndSearch()
    },
    nameOrder: function () {
      this.settedOrder = 'orderByName'
      this.orderByName()
    },
    sizeOrder: function () {
      this.settedOrder = 'orderBySize'
      this.orderBySize()
    },
    langOrder: function () {
      this.settedOrder = 'orderByLang'
      this.orderByLang()
    },
    timeOrder: function () {
      this.settedOrder = 'orderByTime'
      this.orderByTime()
    },
    lastModifiedOrder: function () {
      this.settedOrder = 'orderByLastModified'
      this.orderByLastModified()
    },
    'mArea.isMouseDown': {
      handler(value) {
        if (value === false) this.mArea.isDragging = false
      },
      deep: true,
      immediate: true,
    },
    folder(data) {
      const { name: title } = data ?? {}
      const tree = this.foldersTree
      let { path: id } = this.$route

      id = id.split('/')
      id = id.pop()

      const addToCollection = ({ id, parent_folder_id, name }) => {
        const path = `/folders/${id}`

        if (title && !tree.ids.has(id)) {
          tree.ids.add(id)
          tree.collection.set(id, { path, name })
        }

        if (parent_folder_id && !tree.ids.has(parent_folder_id)) {
          addToCollection(_.find(this.mainFolders, { id: parent_folder_id }))
        }
      }

      // is root
      if (this.$route.name === 'Videos') {
        tree.ids = new Set()
        tree.collection = new Map()
      } else if (title) {
        // page opened
        if (!tree.ids.size) {
          addToCollection(this.folder)

          // reorder
          tree.ids = new Set(Array.from(tree.ids).reverse())
        }
        // navigation through (descending)
        else if (!tree.ids.has(id)) addToCollection(this.folder)
        // navigation through (ascending)
        else if (tree.ids.has(id)) {
          for (const value of Array.from(tree.ids).reverse()) {
            if (value === id) break

            tree.ids.delete(value)
            tree.collection.delete(value)
          }
        }
      }
    },
    async allItems() {
      this.getInsideItemsQuantity()
    },
  },
  methods: {
    ...mapActions('user', [
      'addVideo',
      'editVideo',
      'editFolder',
      'removeVideo',
      'addSubFolderVideo',
      'editSubFolderVideo',
      'removeSubFolderVideo',
      'loadDrmGroups',
    ]),
    ...mapActions('session', [
      'signInUser',
      'updateEmailAddress',
      'confirmUpdateEmailAddress',
    ]),
    ...mapActions('video', ['getVideoProfileId']),
    ...mapMutations('user', [
      'UPDATE_GLOBAL_SNACKBAR',
      'UPDATE_FIRST_ACCESS',
      'UPDATE_ROUTED_VIDEO',
      'UPDATE_MAIN_VIDEOS',
      'UPDATE_MAIN_FOLDERS',
      'UPDATE_ALL_FOLDERS',
      'UPDATE_SUB_FOLDER_VIDEOS',
      'UPDATE_SUB_FOLDER',
      'UPDATE_DID_FETCH_VIDEOS',
      'UPDATE_SHOULD_FETCH_ALL_VIDEOS',
      'UPDATE_DELETED_FOLDERS',
    ]),
    ...mapMutations('video', ['UPDATE_VIDEO']),
    bannersDelay() {
      const expiryDate = new Date()
      expiryDate.setHours(expiryDate.getHours() + 12)
      Storage.setItem('bannersInDelayExpery', expiryDate.toISOString())
    },
    getParentFolderDrmGroups(folderId, drmGroupsCallbackInfo = []) {
      const targetFolder = this.mainFolders.filter(
        ({ id }) => id === folderId
      )[0]
      if (targetFolder.parent_folder_id) {
        if (drmGroupsCallbackInfo.length)
          targetFolder.drm_group_ids = [
            ...targetFolder.drm_group_ids,
            ...drmGroupsCallbackInfo,
          ]
        return (
          this.getParentFolderDrmGroups(
            targetFolder.parent_folder_id,
            targetFolder.drm_group_ids
          ) || []
        )
      } else {
        return targetFolder.drm_group_ids || []
      }
    },
    openConvertingLink() {
      window.open(
        'https://help.pandavideo.com/pt-br/article/video-demorando-demais-para-converter-1uvumg4/',
        '_blank'
      )
    },
    async newFolderCount(itemId) {
      let ret = await foldersApi.getFolderVideos(itemId, true)
      this.$set(this.folderVideosCount, itemId, ret.data.videos.length)
    },
    openBlockedLink() {
      window.open(
        'https://help.pandavideo.com/pt-br/article/como-lidar-com-plagio-na-panda-video-scn24v/',
        '_blank'
      )
    },
    handleWidth() {
      let windowWidth = window.innerWidth

      if (windowWidth < 1535) {
        this.columns[0].visible = false
      } else {
        this.columns[0].visible = true
      }

      if (windowWidth < 1440) {
        this.columns[1].visible = false
        this.columns[2].visible = false
      } else {
        this.columns[1].visible = true
        this.columns[2].visible = true
      }

      if (windowWidth < 1245) {
        this.columns[5].visible = false
      } else {
        this.columns[5].visible = true
      }

      if (windowWidth < 1130) {
        this.columns[4].visible = false
      } else {
        this.columns[4].visible = true
      }

      if (windowWidth < 1035) {
        this.columns[6].visible = false
        this.columns[3].visible = false
      } else {
        this.columns[6].visible = true
        this.columns[3].visible = true
      }
    },
    openFilterMenu(event) {
      const windowHeight = window.innerHeight
      const clickY = event.clientY
      this.availableSpaceBelow = windowHeight - clickY - 60

      this.menuX = event.clientX - 100
      this.menuY = clickY + 30

      this.menuVisible = true
    },
    syncListScrollWithHeaderScroll() {
      const _this = this
      if (!this.$refs.dataTableContent?.$el) {
        setTimeout(() => {
          _this.syncListScrollWithHeaderScroll()
        }, 100)
        return
      }
      this.$refs.dataTableContent.$el.onscroll = () => {
        _this.$refs.dataTableHeader.$el.scrollLeft =
          this.$refs.dataTableContent.$el.scrollLeft
      }
    },
    async handleVisibilityChange() {
      if (this.filteredVids.length > 0) return
      if (this.isUpdatingVisibility) return
      this.isUpdatingVisibility = true
      await this.getAllAccountVideos()
      this.checkAndSearch()
      this.isUpdatingVisibility = false
    },
    getTooltipDirection(index) {
      if (this.allItems.length > 11 && this.allItems.length - index <= 3) {
        return 'top'
      }
      return 'bottom'
    },
    highlightRow(e, option = 'in') {
      const el = (e.target ?? e).closest('.item-line')
      const items = [...document.querySelectorAll('.item-line.rowIsDropOver')]

      for (const item of items) {
        if (item !== el) item.classList.remove('rowIsDropOver')
      }

      el.classList[option === 'in' ? 'add' : 'remove']('rowIsDropOver')
    },
    animateDraggedItems(e) {
      const getElement = (target) => document.querySelector(target)

      const mouseArea = getElement('.mousearea')
      const mouseDrag = getElement('.mousearea__dragging')

      if (mouseArea && mouseDrag) {
        const rect = mouseArea.getBoundingClientRect()

        const scrollPosition = this.containerElement
          ? this.containerElement.scrollTop
          : window.scrollY
        const axisY = e.clientY - rect.top + scrollPosition
        const axisX = e.clientX - rect.left

        mouseDrag.style = `top:${axisY}px;left:${axisX}px;transform:translateX(3px);`
      } else {
        return setTimeout(() => {
          this.animateDraggedItems(e)
        }, 250)
      }

      if (mouseDrag.classList.contains('shrink')) return

      mouseDrag.classList.add('shrink')
    },
    removeModal() {
      this.handleRemoveItem()
      this.showFloatMenu = false
    },
    subModal() {
      this.handleOpenDubModal()
      this.showFloatMenu = false
    },
    dubModal() {
      this.handleOpenDubModal()
      this.showFloatMenu = false
    },
    moveModal() {
      this.handleMoveFiles()
      this.showFloatMenu = false
    },
    duplicateModal() {
      this.openDuplicateVideoModal = true
      this.showFloatMenu = false
    },
    handleClose() {
      this.moveConfirmation = false
      this.resetMovedSetting()
      this.resetMArea()
    },
    resetMovedSetting() {
      this.selectedFolder = {}
      this.movedItems = []
      this.oldFolderId = ''
    },
    resetMArea() {
      this.mArea.isDragging = false
      this.mArea.isSelecting = false
      this.mArea.isMouseDown = false
      this.mArea.startTime = null
      this.mArea.x1 = 0
      this.mArea.y1 = 0
      this.mArea.x2 = 0
      this.mArea.y2 = 0
      this.mArea.isPosX = null
      this.mArea.isPosY = null
      this.mArea.style =
        'display:none;width:0.1px;height:0.1px;top:0;left:0;opacity:0'
      this.mArea.firstDragElem = false
    },
    resetKeySelect() {
      this.keySelect.selectedIndex = null
      this.keySelect.arrowKey = 'ArrowDown'
    },
    async confirmMove() {
      await this.handleMovedItems()
      await this.checkAndSearch()
      this.handleClose()
    },
    handlePreviewMouseleave() {
      clearTimeout(this.clearPreviewTimeout)
      this.clearPreviewTimeout = setTimeout(() => {
        this.showPreview = false
        this.selectedPreview = false
      }, 600)
    },
    handlePreviewMousemove(e, row) {
      if (this.tooltipVisible) return
      const closest = (key) => e.target.closest(key)
      if (row.status == 'FAILED') {
        {
          this.showPreview = false
          this.selectedPreview = false
          return
        }
      }

      if (
        this.selectedItems.length < 2 &&
        this.selectedPreview &&
        row.hasOwnProperty('preview') &&
        this.selectedPreview == row.preview
      ) {
        this.x = e.clientX + 15
      }

      if (
        this.selectedItems.length < 2 &&
        !this.selectedPreview &&
        row.hasOwnProperty('preview') &&
        !this.showPreview
      ) {
        clearTimeout(this.clearPreviewTimeout)
        clearTimeout(this.previewTimeout)
        this.previewTimeout = setTimeout(() => {
          const line = closest('.item-line')?.getBoundingClientRect()

          if (line) {
            this.y = line.top + line.height + 5
            this.x = e.clientX + 15
            this.selectedPreview = row.preview

            this.showPreview = false
            this.$nextTick(() => {
              this.showPreview = true
            })
          }
        }, 500)
      } else if (
        this.selectedItems.length >= 2 ||
        (this.selectedPreview && this.selectedPreview != row.preview)
      ) {
        this.showPreview = false
        this.selectedPreview = false
      }
    },
    handleDrop(e, row) {
      e.preventDefault()
      e.stopPropagation()

      this.highlightRow(e, 'out')
      this.resetMArea()

      if (row.name && !_.find(this.selectedItems, { id: row.id })) {
        this.selectedFolder = row
        this.confirmMove()
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t('message.moveSuccess'),
          type: 'success',
        })
      }
    },
    handleDragLeave(e) {
      this.showPreview = false
      e.preventDefault()
      e.stopPropagation()

      this.highlightRow(e, 'out')
    },
    handleDragOver(e) {
      this.showPreview = false
      e.preventDefault()
      e.stopPropagation()

      this.animateDraggedItems(e)
      this.highlightRow(e)
    },
    handleDragEnter(e) {
      this.showPreview = false
      e.preventDefault()
      e.stopPropagation()
    },
    handleDragStart(e, data) {
      this.showPreview = false
      e.dataTransfer.setData('draggedItem', data)
      e.dataTransfer.setDragImage(newGhostImg, 0, 0)

      let arr = [data]

      this.resetMovedSetting()

      clearTimeout(this.mArea.timeoutDrag)
      this.mArea.timeoutDrag = setTimeout(() => {
        this.animateDraggedItems(e)

        if (this.selectedItems.length) {
          arr = [...this.selectedItems]
        }

        this.movedItems.push(...arr)
      }, 1)
    },
    handleDragEnd(e) {
      this.showPreview = false
      e.preventDefault()
      e.stopPropagation()
    },
    handleMousedown(e) {
      const closest = (key) => e.target.closest(key)
      const getElement = (target) => document.querySelector(target)

      // prevent actions if ...
      if (
        e.detail === 2 ||
        e.which === 3 ||
        closest('.v-simple-checkbox') ||
        closest('.buttons') ||
        closest('.autocomplete') ||
        (closest('.data-table__header') &&
          (closest('i') || closest('.caption'))) ||
        (closest('.page-view__header') &&
          (closest('.base-button') ||
            closest('.header__title') ||
            closest('i') ||
            closest('.v-tab')))
      )
        return e.preventDefault()

      // reset selectAll if not...
      if (
        !(
          closest('.item-line') ||
          closest('.base-button') ||
          closest('.mdi') ||
          e.target.type === 'text' ||
          e.ctrlKey ||
          e.metaKey ||
          e.shiftKey ||
          this.keySelect.isShiftKeyPressed ||
          this.keySelect.isCtrlKeyPressed
        )
      ) {
        this.unselectAllItems()
      }

      const mouseArea = getElement('.mousearea')
      const rect = mouseArea.getBoundingClientRect()
      const index =
        (closest('.data-index') && closest('.data-index').dataset.index) || null

      this.mArea.x1 = e.clientX - rect.left
      this.mArea.y1 = e.clientY - rect.top
      this.mArea.startTime = Date.now()
      this.mArea.isMouseDown = true

      // check keyModifiers
      if (index) {
        const item = this.allItems[index]

        if (this.keySelect.isShiftKeyPressed) {
          const start = Math.min(
            this.keySelect.lastIdxSelected,
            parseInt(index)
          )
          const end = Math.max(this.keySelect.lastIdxSelected, parseInt(index))

          if (this.keySelect.lastShiftIdxSelected >= 0) {
            const startLastShift = Math.min(
              this.keySelect.lastIdxSelected,
              this.keySelect.lastShiftIdxSelected
            )
            const endLastShift = Math.max(
              this.keySelect.lastIdxSelected,
              this.keySelect.lastShiftIdxSelected
            )

            for (let i = startLastShift; i <= endLastShift; i++) {
              const itemShift = this.allItems[i]
              const idxItemSelected = _.findIndex(this.selectedItems, {
                id: itemShift.id,
              })
              if (idxItemSelected >= 0) {
                this.selectedItems.splice(idxItemSelected, 1)
              }
            }
          }

          for (let x = start; x <= end; x++) {
            const item = this.allItems[x]
            if (!_.find(this.selectedItems, { id: item.id })) {
              this.selectedItems.push(item)
            }
          }

          this.keySelect.lastShiftIdxSelected = parseInt(index)
          return
        } else if (this.keySelect.isCtrlKeyPressed) {
          if (_.find(this.selectedItems, { id: item.id })) {
            _.remove(this.selectedItems, ({ id }) => id === item.id)
            this.selectedItems = [...this.selectedItems]
          } else {
            this.selectedItems.push(item)
          }
          this.keySelect.lastIdxSelected = parseInt(index)

          return
        }

        window.clearTimeout(this.mArea.timeoutMove)
        if (item.name) {
          this.mArea.timeoutMove = setTimeout(async () => {
            try {
              this.isLoading = true
              if (!this.mArea.isDragging) {
                this.unselectAllItems()
                this.selectedItems.push(item)
                await this.getFolderVideos(item.id, true)
                this.keySelect.lastIdxSelected = parseInt(index)

                if (this.selectedItems.length === 1) {
                  this.videoToBeDuplicated = this.selectedItems[0].id
                }
              }
            } finally {
              this.isLoading = false
            }
          }, 1)
        }
      }

      // check if is drag action
      window.clearTimeout(this.mArea.timeoutDown)
      this.mArea.timeoutDown = setTimeout(() => {
        if (
          this.mArea.isMouseDown &&
          !this.mArea.isDragging &&
          !this.mArea.isSelecting &&
          !e.shiftKey &&
          e.target.closest('.item-line')
        ) {
          this.mArea.isDragging = true
        }
      }, this.mArea.thresholdTime)
    },
    handleMousemove(e) {
      const closest = (key) => e.target.closest(key)

      const timeElapsed = Date.now() - this.mArea.startTime

      window.clearTimeout(this.mArea.timeoutMove)

      if (this.isDragging) return

      // check movement type
      if (this.mArea.isMouseDown) {
        /** draging */
        if (!this.mArea.isSelecting) {
          // ... is drag'n selected item
          if (
            closest('.rowIsSelected') &&
            !(
              this.keySelect.isShiftKeyPressed ||
              this.keySelect.isCtrlKeyPressed
            )
          ) {
            this.mArea.isDragging = true
            return
          }

          // ... is drag'n by time elapsed
          if (closest('.item-line') && timeElapsed > this.mArea.thresholdTime) {
            this.mArea.isDragging = true
            return
          }
        }
      }
    },
    handleMouseup(e) {
      e.preventDefault()

      this.resetMovedSetting()
      this.resetMArea()
    },
    handleMouseleave(e) {
      e.preventDefault()
      e.stopPropagation()

      if (this.mArea.isSelecting) {
        this.resetMovedSetting()
        this.resetMArea()
      }
    },
    emitBack() {
      this.$router.push({ name: 'Videos' })
    },
    emitBackFolder() {
      this.$router.push({ name: 'Folders', params: { id: this.lastFolder.id } })
    },
    preventKeyEvents() {
      const isMoveConfirmModal = this.moveConfirmation
      const isDuplicateModal = this.openDuplicateVideoModal
      const isEmbedModal = this.showMenuEmbed
      const isExternalVideoModal = this.openAddExternalVideoModal
      const isAnyOtherModal = this.keySelect.isAnyOtherModal

      return (
        isMoveConfirmModal ||
        isDuplicateModal ||
        isEmbedModal ||
        isExternalVideoModal ||
        isAnyOtherModal
      )
    },
    handleKeyUp(e) {
      if (!e.shiftKey) {
        this.keySelect.isShiftKeyPressed = false
        this.keySelect.lastShiftIdxSelected = false
      }
      if (!e.metaKey && !e.ctrlKey) this.keySelect.isCtrlKeyPressed = false
    },
    handleKeyDown(e) {
      if (this.preventKeyEvents()) return

      if (e.shiftKey) this.keySelect.isShiftKeyPressed = true
      if (e.metaKey || e.ctrlKey) this.keySelect.isCtrlKeyPressed = true

      if (
        (e.ctrlKey || e.code === 'ControlLeft' || e.metaKey) &&
        e.key === 'a' &&
        !this.isRenameModalOpen
      ) {
        e.preventDefault()

        if (this.$crisp.is('chat:opened')) {
          const crispInput = document.querySelector('#crisp-chatbox textarea')
          if (crispInput) {
            crispInput.select()
          }
        } else {
          this.selectAllItems()
        }

        return
      }

      if (
        this.keySelect.isShiftKeyPressed &&
        ['ArrowUp', 'ArrowDown'].includes(e.key)
      ) {
        if (!this.keySelect.lastShiftIdxSelected)
          this.keySelect.lastShiftIdxSelected = this.keySelect.lastIdxSelected

        const nextIndex =
          e.key === 'ArrowDown'
            ? this.keySelect.lastShiftIdxSelected + 1
            : this.keySelect.lastShiftIdxSelected - 1 || 0

        const start = Math.min(this.keySelect.lastIdxSelected, nextIndex)
        const end = Math.max(this.keySelect.lastIdxSelected, nextIndex)

        if (this.keySelect.lastShiftIdxSelected >= 0) {
          const startLastShift = Math.min(
            this.keySelect.lastIdxSelected,
            this.keySelect.lastShiftIdxSelected
          )
          const endLastShift = Math.max(
            this.keySelect.lastIdxSelected,
            this.keySelect.lastShiftIdxSelected
          )

          for (let i = startLastShift; i <= endLastShift; i++) {
            const itemShift = this.allItems[i]
            const idxItemSelected = _.findIndex(this.selectedItems, {
              id: itemShift.id,
            })
            if (idxItemSelected >= 0) {
              this.selectedItems.splice(idxItemSelected, 1)
            }
          }
        }

        for (let x = start; x <= end; x++) {
          const item = this.allItems[x]
          if (!_.find(this.selectedItems, { id: item.id })) {
            this.selectedItems.push(item)
          }
        }

        this.keySelect.lastShiftIdxSelected = nextIndex
        return
      }

      if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
        const index =
          this.keySelect.lastIdxSelected !== null
            ? e.key === 'ArrowDown'
              ? this.keySelect.lastIdxSelected + 1
              : this.keySelect.lastIdxSelected - 1
            : 0

        this.keySelect.lastIdxSelected = parseInt(index)
        this.setKeySelectIndex(index, e.key)
        this.handleArrowSelection()
      }
    },
    handleArrowSelection() {
      if (this.selectedItems.length) this.unselectAllItems()

      const item = this.allItems[this.keySelect.lastIdxSelected]
      if (item) this.checkboxSelection(item)
    },
    setKeySelectIndex(index, arrow = null) {
      this.keySelect.lastIdxSelected = index < 0 ? 0 : index
      this.keySelect.selectedIndex = index < 0 ? 0 : index
      this.keySelect.arrowKey = arrow ?? 'ArrowDown'
    },
    async handleMovedItems() {
      this.isRelocating = true

      const newParentFolderId = this.selectedFolder.id
      const toBeMovedItems = []
      const toBeMovedFolders = []

      for (let item of this.movedItems) {
        if (item.title) {
          toBeMovedItems.push({
            video_id: item.id,
            folder_id: newParentFolderId,
          })

          this.oldFolderId = item.folder_id
        }

        if (item.name) toBeMovedFolders.push(item)
      }

      if (toBeMovedItems.length > 0)
        await this.moveVideoToFolder(toBeMovedItems)

      if (toBeMovedFolders.length > 0)
        await this.updateFolders(toBeMovedFolders)

      this.isRelocating = false
    },
    async moveVideoToFolder(videos) {
      try {
        const ret = await videosApi.updateMultipleVideos(videos)

        const updatedVideos = []
        let foldersResult = null

        if (videos.length < 2) updatedVideos.push(ret.data)
        else updatedVideos.push(...ret.data)

        if (this.oldFolderId) {
          foldersResult = this.mainFolders.map((folder) =>
            folder.id === this.oldFolderId
              ? {
                  ...folder,
                  videos_count: folder.videos_count - updatedVideos.length,
                }
              : folder
          )
          this.UPDATE_MAIN_FOLDERS(foldersResult)
        }

        let addedId = updatedVideos[0].folder_id

        if (addedId) {
          foldersResult = this.mainFolders.map((folder) =>
            folder.id === addedId
              ? {
                  ...folder,
                  videos_count:
                    parseInt(folder.videos_count) +
                    parseInt(updatedVideos.length),
                }
              : folder
          )
          this.UPDATE_MAIN_FOLDERS(foldersResult)
        }

        this.$root.$emit('moveFilesVideosPage', updatedVideos)
      } catch (e) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `${e}`,
        })
      }
    },
    async updateFolders(folders) {
      const newParentFolderId = this.selectedFolder.id

      for (let i = 0; i < folders.length; i++) {
        try {
          const ret = await foldersApi.updateFolder(
            { parent_folder_id: newParentFolderId },
            folders[i].id
          )

          const foldersResult = this.mainFolders.map((folder) =>
            folder.id === folders[i].id
              ? { ...folder, videos_count: folders[i].videos_count }
              : folder
          )
          this.UPDATE_MAIN_FOLDERS(foldersResult)
          this.editFolder({
            ...ret.data,
            videos_count: folders[i].videos_count,
          })
          this.$root.$emit('removeFolderVideosPage', {
            ...ret.data,
            videos_count: folders[i].videos_count,
          })
        } catch (e) {
          console.error(e)
        }
      }
    },
    async handleEmailConfirmChange(params) {
      if (params.uuid) {
        try {
          await this.confirmUpdateEmailAddress(params.uuid)

          this.$root.$emit('refreshUserPlan')

          this.$root.$on('showAccountWarning', () => {
            this.$root.$emit('openMyProfileModal', {
              editing: 'email',
              step: 3,
              success: true,
            })
          })
        } catch (e) {
          console.error(e)

          let step = 7

          if (e.detail === 'invalid token') {
            step = 8
          }

          this.$root.$emit('openMyProfileModal', {
            editing: 'email',
            step,
            success: false,
            error: e.response.data,
          })
        }
      }
    },
    paramsToObject(entries) {
      const result = {}
      for (const [key, value] of entries) {
        result[key] = value
      }
      return result
    },
    // require param to be reactively mutable
    verifyPermissions(permissions) {
      return (
        permissions.includes('create_video') ||
        permissions.includes('create-video')
      )
    },
    resizeTimeout() {
      clearTimeout(this.resizeWindowTimeout)
      this.resizeWindowTimeout = setTimeout(() => {
        this.setTableHeight()
      }, 300)
    },
    setTableHeight() {
      // prevent error using $refs (HTML not loaded)
      const getEl = (target) => document.querySelector(target)

      const actions = getEl('.data-table__actions')
      const header = getEl('.data-table__header')
      const content = getEl('.page-view__content')
      const line = getEl('.item-line')

      const aHeight = actions?.clientHeight || 65
      const hHeight = header?.clientHeight || 45
      const cHeight = content?.clientHeight || 600
      const lHeight = line?.clientHeight || 48

      const cPaddingTop = window
        .getComputedStyle(content, null)
        .getPropertyValue('padding-top')

      const differ = aHeight + hHeight - parseInt(cPaddingTop, 10)

      this.tableHeight = Math.abs(
        cHeight - differ - ((cHeight - differ) % lHeight)
      )
    },
    handleOpenAddExternalVideo() {
      this.openAddExternalVideoModal = true
    },
    async setTrashItems() {
      const deletedFolders = [...this.deletedFolders]
      const deletedVideos = await window.db.videos
        .where({ status: 'DELETING' })
        .toArray()

      this.allDeletedItems = deletedFolders.concat(deletedVideos)
    },
    getSubKey() {
      let subKey = null
      let keys = Storage.getStorageKeys()
      for (let key of keys) {
        if (key[0].includes('LastAuthUser')) {
          subKey = key[1]
        }
      }
      return subKey
    },
    async pageRequests() {
      if (!window.db) {
        this.$root.$emit('initDexie')
      }
      this.isFetching = true
      if (
        !this.$store.state.user.didFetchVideos ||
        this.$store.state.user.shouldFetchAllVideos
      ) {
        await this.readUserFolders()
        await this.getAllAccountVideos()
        this.UPDATE_DID_FETCH_VIDEOS(true)
        this.UPDATE_SHOULD_FETCH_ALL_VIDEOS(false)
      }
      this.checkIfStorageQueueIsUpdated()
      this.checkAndSearch()
    },
    async checkIfStorageQueueIsUpdated() {
      let storagedVideosArray = Storage.getItem(
        `panda-queue-${this.$store.state.user.userInfo.sub ?? this.getSubKey()}`
      )
      if (!storagedVideosArray) return
      for (let video of storagedVideosArray) {
        let ret = await window.db.videos.get(video.id)
        if (ret) {
          storagedVideosArray = storagedVideosArray.filter(
            (storageVideo) => storageVideo.id != ret.id
          )
        } else {
          this.$root.$emit('addVideoVideosPage', video)
        }
      }
      Storage.setItem(
        `panda-queue-${
          this.$store.state.user.userInfo.sub ?? this.getSubKey()
        }`,
        storagedVideosArray
      )
    },
    async fetchEmbedVideo() {
      this.fetching = true
      Storage.setItem('width', this.embedVideo.width)
      Storage.setItem('height', this.embedVideo.height)
      let storageEmbedOption = Storage.getItem('embedType')
      let embedOptionsValue = this.embedOptions.map((el) => el.value)
      let storageIndex = embedOptionsValue.indexOf(storageEmbedOption)
      if (storageIndex !== -1) {
        this.embedSelected = this.embedOptions[storageIndex].value
      } else {
        this.embedSelected = this.embedOptions[0].value
      }
      Storage.setItem('embedType', this.embedSelected)
      let pullzoneValue = Storage.getItem(
        `pullzone-${this.$store.state.user.userInfo.sub ?? this.getSubKey()}`
      )
      this.userPullzoneName = pullzoneValue
      this.authToken = Storage.getItem('accessToken')
      this.$root.$on(
        'changeEmbed',
        function () {
          this.embedSelected = Storage.getItem('embedType')
            ? Storage.getItem('embedType')
            : i18n.t('message.pages.videoEditModal.embedConfiguration.default')
        }.bind(this)
      )
      this.fetching = false
    },
    async openCustomizeEmbed(menuItem) {
      this.menuPosition = {
        link: {
          x: window.innerWidth / 2 - 300,
          y: 140,
        },
        embed: {
          x: window.innerWidth / 2 - 300,
          y: 140,
        },
        dots: {
          x: 1500,
          y: 140,
        },
      }
      this.mustShowEmbedModal = false
      this.video = menuItem
      this.timeVideo = menuItem.length ?? 0
      this.fetching = true
      this.videoId = menuItem.id
      this.showMenu = false
      this.showMenuEmbed = true
      setTimeout(
        function () {
          this.mustShowEmbedModal = true
        }.bind(this)
      )
      this.fetchEmbedVideo()
    },
    removeDotIfItExists(value) {
      let dotIndex = value.indexOf('.')
      if (dotIndex === -1) return value
      return value.slice(0, dotIndex) + value.slice(dotIndex + 2, value.length)
    },
    handleDrmMenu(item) {
      this.$root.$emit('drmModalDashboard', {
        item: item,
        groups: this.drmGroups,
      })
      this.keySelect.isAnyOtherModal = true
    },
    findDrmGroupById(id) {
      let foundGroup = this.drmGroups.find((el) => el.id === id)
      return foundGroup
    },
    isAnyDrmGroupActive(ids) {
      if (!ids || !Object.keys(ids).length) return false
      return ids.some((id) => id.active)
    },
    orderByLastModified() {
      if (this.lastModifiedOrder === 'asc') {
        this.loadedFolders.sort(
          (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
        )
        this.loadedVideos.sort(
          (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
        )
      }
      if (this.lastModifiedOrder === 'desc') {
        this.loadedFolders.sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        )
        this.loadedVideos.sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        )
      }
    },
    handleOrderByLastModified() {
      if (!this.lastModifiedOrder || this.lastModifiedOrder === 'desc')
        return (this.lastModifiedOrder = 'asc')
      if (this.lastModifiedOrder === 'asc')
        return (this.lastModifiedOrder = 'desc')
    },
    orderByTime() {
      if (this.timeOrder === 'asc') {
        this.loadedVideos.sort((a, b) => a.length - b.length)
        this.loadedFolders.sort((a, b) => {
          const aTime = this.getFolderExtraInfoById(a.id, 'length')
          const bTime = this.getFolderExtraInfoById(b.id, 'length')
          return aTime - bTime
        })
      }
      if (this.timeOrder === 'desc') {
        this.loadedVideos.sort((a, b) => b.length - a.length)
        this.loadedFolders.sort((a, b) => {
          const aTime = this.getFolderExtraInfoById(a.id, 'length')
          const bTime = this.getFolderExtraInfoById(b.id, 'length')
          return bTime - aTime
        })
      }
    },
    handleOrderByTime() {
      if (!this.timeOrder || this.timeOrder === 'desc')
        return (this.timeOrder = 'asc')
      if (this.timeOrder === 'asc') return (this.timeOrder = 'desc')
    },
    getFolderExtraInfoById(id, field) {
      return this.folderData.get(id)[field]
    },
    orderBySize() {
      if (this.sizeOrder === 'asc') {
        this.loadedVideos.sort((a, b) => a.storage_size - b.storage_size)
        this.loadedFolders.sort((a, b) => {
          const aSize = this.getFolderExtraInfoById(a.id, 'size')
          const bSize = this.getFolderExtraInfoById(b.id, 'size')
          return aSize - bSize
        })
      }
      if (this.sizeOrder === 'desc') {
        this.loadedVideos.sort((a, b) => b.storage_size - a.storage_size)
        this.loadedFolders.sort((a, b) => {
          const aSize = this.getFolderExtraInfoById(a.id, 'size')
          const bSize = this.getFolderExtraInfoById(b.id, 'size')
          return bSize - aSize
        })
      }
    },
    orderByLang() {
      if (this.langOrder === 'asc') {
        this.loadedVideos.sort((a, b) => {
          if (a.original_lang) {
            return 1
          } else if (b.original_lang) {
            return -1
          }
          return 0
        })
      }
      if (this.langOrder === 'desc') {
        this.loadedVideos.sort((a, b) => {
          if (a.original_lang) {
            return -1
          } else if (b.original_lang) {
            return 1
          }
          return 0
        })
      }
    },
    handleOrderBySize() {
      if (!this.sizeOrder || this.sizeOrder === 'desc')
        return (this.sizeOrder = 'asc')
      if (this.sizeOrder === 'asc') return (this.sizeOrder = 'desc')
    },
    handleOrderByLang() {
      if (!this.langOrder || this.langOrder === 'desc')
        return (this.langOrder = 'asc')
      if (this.langOrder === 'asc') return (this.langOrder = 'desc')
    },
    orderByName() {
      if (this.nameOrder === 'asc') {
        this.loadedVideos.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { numeric: true })
        )
        this.loadedFolders.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
      if (this.nameOrder === 'desc') {
        this.loadedVideos.sort(
          (a, b) =>
            -a.title.localeCompare(b.title, undefined, { numeric: true })
        )
        this.loadedFolders.sort(
          (a, b) => -a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
    },
    handleOrderByName() {
      if (!this.nameOrder || this.nameOrder === 'desc')
        return (this.nameOrder = 'asc')
      if (this.nameOrder === 'asc') return (this.nameOrder = 'desc')
    },
    handleClickRow(item) {
      this.handleDoubleClickRow(item)
    },
    async dubInListing(item) {
      this.selectedItems = [item]
      await this.$nextTick()
      this.showFloatMenu = false
      await this.handleSelectedItems()
      this.handleOpenDubModal()
    },
    async subInListing(item) {
      this.selectedItems = [item]
      await this.$nextTick()
      this.showFloatMenu = false
      await this.handleSelectedItems()
      this.handleOpenSubModal()
    },
    async checkAndSearch() {
      this.selectedItems = []
      document.body.style.cursor = 'wait'
      if (!this.mainFolders.length) {
        this.isFetching = true
        await this.readUserFolders()
      }
      if (this.$route.params.id) {
        this.listQuery.folder_id = this.$route.params.id
      } else {
        this.listQuery.folder_id = null
        this.listQuery.root_folder = 1
      }
      let totalLength = await window.db.videos.count()
      let deletedLength = 0
      try {
        deletedLength = await window.db.videos
          .where({ status: 'DELETING' })
          .count()
        this.deletedCount = deletedLength
      } catch (e) {
        if (!this.dbFailed) {
          this.dbFailed = true
          await Dexie.delete(
            `panda-${this.$store.state.user.userInfo.sub ?? this.getSubKey()}`
          )
          this.UPDATE_DID_FETCH_VIDEOS(false)
          this.UPDATE_SHOULD_FETCH_ALL_VIDEOS(true)
          this.pageRequests()
        }
      }
      this.dbVideosLength = totalLength - deletedLength
      await this.setPageVideos()

      // update folders size and length
      if (this.loadedFolders) {
        this.folderData = await this.getFolderSize(this.loadedFolders)
      }

      this.isFetching = false
      this.syncListScrollWithHeaderScroll()
    },
    getFolderInfo(id) {
      this.folder = this.findFolderById(id)
      if (this.folder?.parent_folder_id) {
        this.lastFolder = this.findFolderById(this.folder.parent_folder_id)
      } else {
        this.lastFolder = {
          name: '',
          updated_at: '',
          created_at: '',
          parent_folder_id: '',
          videos: [],
          id: '',
        }
      }
    },
    show(item, e) {
      this.menuVisible = false
      const windowHeight = window.innerHeight
      const clickY = event.clientY
      this.availableSpaceBelow = windowHeight - clickY - 60
      this.screenHeight = windowHeight
      this.isExternalSourceVideo = item.hasOwnProperty('video_source')
      this.menuItem = item
      this.videoId = item.id
      this.videoToBeDuplicated = item.id
      item.status === 'DRAFT'
        ? (this.disableVideoCopyLinks = true)
        : (this.disableVideoCopyLinks = false)
      e.preventDefault()
      this.showMenu = false
      this.xMenu = e.clientX
      this.yMenu = e.clientY
      this.x = e.clientX
      this.y = e.clientY
      this.$nextTick(() => {
        this.showMenu = true
      })
    },
    async getFolderItems(folderId) {
      let folderVideos = await window.db.videos
        .where({ folder_id: folderId })
        .toArray()
      if (!folderVideos) {
        folderVideos = []
      }
      let folderFolders = this.mainFolders.filter((folder) => {
        return folder.parent_folder_id === folderId
      })
      if (!folderFolders) {
        folderFolders = []
      }
      await this.addItemsToCsv({
        folders: folderFolders,
        videos: folderVideos,
      })
    },
    async addItemsToCsv(data) {
      if (data.videos.length > 0) {
        for (let video of data.videos) {
          this.csvItems.push(video)
        }
      }
      if (data.folders.length > 0) {
        for (let folder of data.folders) {
          await this.getFolderItems(folder.id)
        }
      }
    },
    async downloadCSV() {
      this.csvLoading = true
      let folderFolders = []
      let folderVideos = []
      for (let item of this.selectedItems) {
        if (item.name) {
          folderFolders.push(item)
        }
        if (item.title) {
          folderVideos.push(item)
        }
      }
      await this.addItemsToCsv({
        folders: folderFolders,
        videos: folderVideos,
      })
      let csvItems = this.csvItems.filter((item) => {
        return item && item.title
      })
      const videos = csvItems.map((video) => ({
        id: `"${video.id}"`,
        title: `"${video.title}"`,
        description: `"${video.description ? video.description : ''}"`,
        videoExternalId: `"${video.video_external_id}"`,
        embed: `"${this.getEmbedCode(video)}"`,
        hls: `"https://b-${video.pullzone_name}.tv.pandavideo.com.br/${video.video_external_id}/playlist.m3u8"`,
        player: `"https://player-${video.pullzone_name}.tv.pandavideo.com.br/embed/?v=${video.video_external_id}"`,
        status: `"${video.status}"`,
        duration: `"${video.length}"`,
      }))
      let csv = Object.keys(videos[0]).join(',') + '\n'
      for (let i = 0; i < videos.length; i++) {
        csv += Object.values(videos[i]).join(',') + '\n'
      }
      const anchor = document.createElement('a')
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
      anchor.target = '_blank'
      anchor.download = 'pv_rootvideos.csv'
      anchor.click()
      this.csvItems = []
      this.selectedItems = []
      this.csvLoading = false
    },
    async statusCheck() {
      if (document.visibilityState && document.visibilityState != 'visible')
        return
      let toBeCheckedVideos = this.loadedVideos.filter((video) => {
        if (
          this.videoIdsGetStatus[video.id] &&
          video.status != 'CONVERTED' &&
          video.status != 'FAILED'
        ) {
          return video
        } else {
          return false
        }
      })
      if (toBeCheckedVideos.length < 1) {
        return
      }
      let limitedAmount = toBeCheckedVideos.slice(0, 2)
      let promises = limitedAmount.map(this.checkVideo)
      await Promise.all(promises)
    },
    async checkVideo(video) {
      try {
        if (!video.id) return
        const { data: incomingVideo } = await videosApi.getAVideo(video.id)
        if (
          (incomingVideo.created_at && !video.created_at) ||
          (incomingVideo.created_at && incomingVideo.status !== video.status)
        ) {
          if (!incomingVideo.folder_id) {
            incomingVideo['folder_id'] = 'root'
          }
          this.updateVideoInArray(incomingVideo)
        }
      } catch (e) {
        console.error(e)
      }
    },
    async updateVideoInArray(video) {
      this.loadedVideos = this.loadedVideos.map((item) =>
        item.id == video.id ? video : item
      )
      const aiWorkflowProps = { aiWorkflow: { isEnabled: false } }
      if (!video.hasOwnProperty('aiWorkflow'))
        Object.assign(video, aiWorkflowProps)
      await window.db.videos.update(video.id, video)
      this.$root.$emit('fixUserLimit')
    },
    handleSettedOrder() {
      if (this.settedOrder == 'orderBySize') {
        this.orderBySize()
      }
      if (this.settedOrder == 'orderByLang') {
        this.orderByLang()
      }
      if (this.settedOrder == 'orderByTime') {
        this.orderByTime()
      }
      if (this.settedOrder == 'orderByLastModified') {
        this.orderByLastModified()
      }
      if (this.settedOrder == 'orderByName' || this.settedOrder == '') {
        this.orderByName()
      }
    },
    async readAllFiles() {
      this.UPDATE_DID_FETCH_VIDEOS(false)
      this.UPDATE_SHOULD_FETCH_ALL_VIDEOS(true)
      this.error.getFilesError = false
      await this.pageRequests()
    },
    handleAddFile(data) {
      if (!this.hasEditVideoPerm) return
      if (!this.planInfo.subscription.active) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t('message.errors.unregularAccountUpload'),
        })
        return
      }
      const hasLimitToUpload = this.verifyIfHasLimitToUpload({
        limit: this.planInfo?.limit,
      })
      if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status !== 'trialing'
      ) {
        this.$root.$emit('usageLimitModal', {
          open: true,
          storagePlan: this.planInfo?.limit?.storage_plan,
          type: 'storage',
        })
        return
      } else if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status === 'trialing'
      ) {
        this.$root.$emit('ModalResetBillingCycle')
        return
      }
      const hasTrafficToUpload = this.verifyIfHasLimitTraffic({
        limit: this.planInfo?.limit,
      })
      if (
        !hasTrafficToUpload &&
        this.planInfo.subscription.status !== 'trialing' &&
        !this.planInfo.limit.trusted_charge_pos
      ) {
        this.$root.$emit('usageLimitModal', {
          open: true,
          storagePlan: this.planInfo?.limit?.traffic_plan,
          type: 'traffic',
        })
        return
      } else if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status === 'trialing'
      ) {
        this.$root.$emit('ModalResetBillingCycle')
        return
      }
      this.$root.$emit('addVideo', {
        file: data.file,
        folderId: data.folderId,
      })
    },
    handleAddDrm() {
      this.$root.$emit('drmAddGroupModal', this.selectedItems)
    },
    async handleCreateFolder() {
      this.$root.$emit('folderModal', {})
      this.keySelect.isAnyOtherModal = true
    },
    handleImportModal() {
      if (!this.hasEditVideoPerm) return
      const hasLimitToUpload = this.verifyIfHasLimitToUpload({
        limit: this.planInfo?.limit,
      })
      if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status !== 'trialing'
      ) {
        this.$root.$emit('usageLimitModal', {
          open: true,
          storagePlan: this.planInfo?.limit?.storage_plan,
          type: 'storage',
        })
        return
      } else if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status === 'trialing'
      ) {
        this.$root.$emit('ModalResetBillingCycle')
        return
      }
      const hasTrafficToUpload = this.verifyIfHasLimitTraffic({
        limit: this.planInfo?.limit,
      })
      if (
        !hasTrafficToUpload &&
        this.planInfo.subscription.status !== 'trialing' &&
        !this.planInfo.limit.trusted_charge_pos
      ) {
        this.$root.$emit('usageLimitModal', {
          open: true,
          storagePlan: this.planInfo?.limit?.traffic_plan,
          type: 'traffic',
        })
        return
      } else if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status === 'trialing'
      ) {
        this.$root.$emit('ModalResetBillingCycle')
        return
      }
      this.$root.$emit('importModal', { open: true })
      this.keySelect.isAnyOtherModal = true
    },
    uploadVideo() {
      if (!this.hasEditVideoPerm) return
      const hasLimitToUpload = this.verifyIfHasLimitToUpload({
        limit: this.planInfo?.limit,
      })
      if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status !== 'trialing'
      ) {
        this.$root.$emit('usageLimitModal', {
          open: true,
          storagePlan: this.planInfo?.limit?.storage_plan,
          type: 'storage',
        })
        return
      } else if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status === 'trialing'
      ) {
        this.$root.$emit('ModalResetBillingCycle')
        return
      }
      const hasTrafficToUpload = this.verifyIfHasLimitTraffic({
        limit: this.planInfo?.limit,
      })
      if (
        !hasTrafficToUpload &&
        this.planInfo.subscription.status !== 'trialing' &&
        !this.planInfo.limit.trusted_charge_pos
      ) {
        this.$root.$emit('usageLimitModal', {
          open: true,
          storagePlan: this.planInfo?.limit?.traffic_plan,
          type: 'traffic',
        })
        return
      } else if (
        !hasLimitToUpload &&
        this.planInfo.subscription.status === 'trialing'
      ) {
        this.$root.$emit('ModalResetBillingCycle')
        return
      }
      this.$refs.uploadInput.onchange = async (event) => {
        const files = event.target.files
        for (let file of files) {
          const formData = new FormData()
          formData.append('documentUpload', file)
          this.$root.$emit('addVideo', { file: file })
        }
      }
      this.$refs.uploadInput.click()
    },
    toggleColumnVisibility(item) {
      item.visible = !item.visible
      this.saveColumnsVisibility()
    },
    saveColumnsVisibility() {
      const visibilityConfig = this.columns.map((column) => column.visible)
      localStorage.setItem(
        'columnsVisibility',
        JSON.stringify(visibilityConfig)
      )
    },
    loadColumnsVisibility() {
      const savedVisibility = localStorage.getItem('columnsVisibility')
      if (savedVisibility) {
        const visibilityConfig = JSON.parse(savedVisibility)
        this.columns.forEach((column, index) => {
          column.visible = visibilityConfig[index]
        })
      }
    },
    verifyIfHasLimitToUpload({ limit }) {
      let ret = true
      if (limit?.storage >= limit?.storage_plan) {
        ret = false
      }
      return ret
    },
    verifyIfHasLimitTraffic({ limit }) {
      let ret = true
      if (limit?.traffic >= limit?.traffic_plan) {
        ret = false
      }
      return ret
    },
    async setPageVideos() {
      let pageVideos = []
      if (this.$route.name === 'Videos') {
        pageVideos = await window.db.videos
          .where({ folder_id: 'root' })
          .limit(50)
          .toArray()
      } else {
        const folderId = String(this.listQuery.folder_id || '')
        pageVideos = await window.db.videos
          .where({ folder_id: folderId })
          .limit(50)
          .toArray()
      }
      let folderFolders = []
      if (this.$route.params.id) {
        folderFolders = this.mainFolders.filter((folder) => {
          return folder.parent_folder_id === this.$route.params.id
        })
      } else {
        folderFolders = this.rootFolders
      }
      this.loadedFolders = folderFolders
      this.loadedVideos = pageVideos
      this.orderByName()
      setTimeout(() => {
        this.$route.name === 'Videos'
          ? (this.isFolder = false)
          : (this.isFolder = true)
        this.getFolderInfo(this.$route.params.id)
      }, 200)
      document.body.style.cursor = 'default'
      setTimeout(async () => {
        let newConcatArray = []
        if (pageVideos.length > 49) {
          if (this.$route.name === 'Videos') {
            newConcatArray = await window.db.videos
              .where({ folder_id: 'root' })
              .offset(50)
              .toArray()
          } else {
            newConcatArray = await window.db.videos
              .where({ folder_id: this.listQuery.folder_id })
              .offset(50)
              .toArray()
          }
        }
        this.loadedVideos.push(...newConcatArray)
      }, 2000)
      this.orderByName()

      this.intro_key = 'video'
      const hasSeenAiModal = Storage.getItem('hasSeenAiCtaModal')
      if (!this.allItems.length && this.userFirstAccess) {
        if (!hasSeenAiModal) {
          this.$root.$on('AiCtaModalHasClosed', () => {
            this.setIntroJS({ key: 'video', delay: 350 })
          })
        } else {
          this.setIntroJS({ key: 'video', delay: 350 })
        }
      }
    },
    handleFolders(e) {
      this.openFolderTree = true
      this.menuPosition.x = e.clientX
      this.menuPosition.y = e.clientY
    },
    async videosRequest(page) {
      let queryObject = {
        limit: this.videosLimit,
      }
      if (page) queryObject['page'] = page
      try {
        const ret = await videosApi.getAllVideosAndBin(queryObject)
        return ret
      } catch (error) {
        this.isFetching = false
        this.formatErr(error)
      }
    },
    formatErr(err) {
      this.error.getFilesError = true
      const formattedErr = JSON.parse(JSON.stringify(err))
      let errFormattedText =
        err.msg ??
        err.response?.data?.errMsg ??
        err.response?.data?.message ??
        formattedErr?.message ??
        err ??
        'Error'
      if (this.error.tooltipErrMsg.length) {
        this.error.tooltipErrMsg =
          this.error.tooltipErrMsg + ' | ' + errFormattedText
      } else {
        this.error.tooltipErrMsg = errFormattedText
      }
      this.error.unknownError = true
      this.error.shownErrMsg = i18n.t('message.errors.unexpectedError')
      this.error.fetch = true
    },
    async loopVideos() {
      let shouldFetchAgain = true
      let page = 0
      let videosObject = {}
      try {
        while (shouldFetchAgain) {
          let ret = null
          if (page > 0) {
            ret = await this.videosRequest(page)
          } else {
            ret = await this.videosRequest()
          }
          if (page === 0) {
            videosObject = ret.data.videos
          } else {
            for (let folder in ret.data.videos) {
              if (videosObject[folder]) {
                videosObject[folder].push(...ret.data.videos[folder])
              } else {
                videosObject[folder] = ret.data.videos[folder]
              }
            }
          }
          page++
          if (ret.data.total < this.videosLimit) {
            shouldFetchAgain = false
          }
        }
        return videosObject
      } catch (err) {
        this.formatErr(err)
      }
    },
    async getAllAccountVideos() {
      try {
        const videos = await this.loopVideos()
        let filteredFoldersIds = []
        let videosResponse = videos
        for (let folderId in videosResponse) {
          filteredFoldersIds.push(folderId)
        }
        await window.db.videos.clear()
        for (let folderId of filteredFoldersIds) {
          let arrToBeAdded = videos[folderId].map((obj) => ({
            ...obj,
            folder_id: folderId,
          }))
          await window.db.videos.bulkPut(arrToBeAdded)
        }
      } catch (error) {
        this.isFetching = false
      }
    },
    async readUserFolders() {
      try {
        let ret = await foldersApi.getUserFolders()
        let notDeletedFolders = ret.data.folders.filter(
          (folder) => folder.status != false
        )
        let deletedFolders = ret.data.folders.filter(
          (folder) => folder.status === false
        )
        this.UPDATE_MAIN_FOLDERS(notDeletedFolders)
        this.UPDATE_DELETED_FOLDERS(deletedFolders)
        this.UPDATE_ALL_FOLDERS(ret.data.folders)
      } catch (e) {
        this.formatErr(e)
      }
    },
    async readVideosStatus() {
      try {
        const ret = await videosApi.getVideoStatus()
        for (let i = 0; i < ret.data.length; i++) {
          if (ret.data[i].status === 'CONVERTED')
            this.videosStatus.converted = ret.data[i].count
          if (ret.data[i].status === 'CONVERTING')
            this.videosStatus.converting = ret.data[i].count
          if (ret.data[i].status === 'DRAFT')
            this.videosStatus.draft = ret.data[i].count
          if (ret.data[i].status === 'DELETING')
            this.videosStatus.deleting = ret.data[i].count
          if (ret.data[i].status === 'FAILED')
            this.videosStatus.failed = ret.data[i].count
        }
      } catch (e) {
        console.error(e)
      }
    },
    handleCopyHLSEmbed(video) {
      let hls = `https://b-${video.pullzone_name}.tv.pandavideo.com.br/${video.video_external_id}/playlist.m3u8`
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.embedHlsCopiedToClipboard'),
        type: 'success',
      })
      navigator.clipboard.writeText(`${hls}`)
    },
    handleCopyCodeEmbed(video) {
      const code = video.id
      const open = true
      const text = i18n.t('message.embedCodeCopiedToClipboard')

      this.UPDATE_GLOBAL_SNACKBAR({ open, text, type: 'success' })
      navigator.clipboard.writeText(`${code}`)
    },
    handleCopySrcEmbed(video) {
      const src = video.video_player
      const open = true
      const text = i18n.t('message.embedSrcCopiedToClipboard')

      this.UPDATE_GLOBAL_SNACKBAR({ open, text, type: 'success' })
      navigator.clipboard.writeText(`${src}`)
    },
    handleCopyExternalIdEmbed(video) {
      const src = video.video_external_id
      const open = true
      const text = i18n.t('message.embedSrcCopiedToClipboard')
      this.UPDATE_GLOBAL_SNACKBAR({ open, text, type: 'success' })
      navigator.clipboard.writeText(`${src}`)
    },
    handleShowLink(item) {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.copiedToClipboard'),
        type: 'success',
      })
      navigator.clipboard.writeText(`${item}`)
    },
    handleEmbedCopy() {
      if (this.isExternalSourceVideo) this.handleShowLink(this.embed)
      else if (this.isExternalIdEmbed)
        this.handleCopyExternalIdEmbed(this.menuItem)
      else if (this.srcEmbed) this.handleCopySrcEmbed(this.menuItem)
      else if (this.codeEmbed) this.handleCopyCodeEmbed(this.menuItem)
      else if (this.hlsEmbed) this.handleCopyHLSEmbed(this.menuItem)
      else this.handleShowLink(this.embed)
    },
    handleEditFolder(folder) {
      this.$root.$emit('folderModal', { ...folder, edit: true })
    },
    handleRenameVideo(video) {
      this.$root.$emit('videoModalRename', { ...video, edit: true })
    },
    handleMoveFiles() {
      this.keySelect.isAnyOtherModal = true

      if (this.selectedItems.length) {
        this.$root.$emit('moveModal', this.selectedItems)
        return
      }
      let arr = []
      arr.push(this.menuItem)
      this.$root.$emit('moveModal', arr)
    },
    handleGoToFolder(item) {
      this.$router.push({ name: 'Folders', params: { id: item.id } })
    },
    calcBytes(bytes) {
      if (bytes.length < 10) {
        return `${prettyBytes(bytes)}`
      } else {
        return `${prettyBytes(bytes, { maximumFractionDigits: 0 })}`
      }
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    handleOpenNewTab() {
      let routeData = {}
      if (this.menuItem.title) {
        routeData = this.$router.resolve({
          name: 'VideoPage',
          params: { id: this.menuItem.id },
        })
      }
      if (this.menuItem.name) {
        routeData = this.$router.resolve({
          name: 'Folders',
          params: { id: this.menuItem.id },
        })
      }
      window.open(routeData.href, '_blank')
    },
    openKnowMoreConverting() {
      window.open(PANDA_HELP_CONVERTING, 'blank')
    },
    handleRemoveItem(item) {
      if (this.selectedItems.length) {
        this.$root.$emit('deleteVideoModal', [...this.selectedItems])
      } else {
        let videos = []
        videos.push(item)
        this.$root.$emit('deleteVideoModal', [...videos])
      }
      this.keySelect.isAnyOtherModal = true
    },
    handleControlClick(event, item) {
      if (item.title && item.status !== 'DRAFT') {
        if (event.ctrlKey === true) {
          let routeData = this.$router.resolve({
            name: 'VideoPage',
            params: { id: item.id },
          })
          window.open(routeData.href, '_blank')
        }
      }
    },
    visibilityChanged(isVisible, data) {
      if (
        data &&
        data.status &&
        data.id &&
        data.status != 'CONVERTED' &&
        data.status != 'FAILED'
      ) {
        if (isVisible) {
          this.videoIdsGetStatus[data.id] = true
        } else {
          delete this.videoIdsGetStatus[data.id]
        }
      }
    },
    handleStatusPage() {
      // let loc = this.getLocale()
      // if (loc === 'pt-BR') return window.open(PANDA_STATUS_PAGE_BR, '_blank')
      return window.open(PANDA_STATUS_PAGE, '_blank')
    },
    getInsideItemsQuantity() {
      // reset map
      this.folderItemsQuantityData = new Map()

      for (const { id, name, videos_count } of this.allItems) {
        if (!name) continue

        let qty

        const { length } = this.mainFolders.filter(
          ({ parent_folder_id }) => parent_folder_id === id
        )
        if (!videos_count && length) qty = `(${length})`

        if (!length && videos_count < 1) qty = '(0)'

        const size = parseInt(length) + parseInt(videos_count)

        qty = `(${size})`

        this.folderItemsQuantityData.set(id, qty)
      }
    },
    checkIfSelected(item) {
      let foundItem = this.selectedItems.find((searchedItem) => {
        return searchedItem.id === item.id
      })
      if (foundItem) return true
      return false
    },

    async getFolderVideos(folderId, getLogs) {
      try {
        const ret = await foldersApi.getFolderVideos(folderId, getLogs)
        const hasBlockedVideo = ret.data.videos.some(
          (video) => video.status === 'BLOCKED'
        )
        this.blockFolder = hasBlockedVideo
        return hasBlockedVideo
      } catch (e) {
        console.error(e)
        return false
      }
    },

    checkboxSelection: async function (item) {
      this.isLoading = true
      try {
        let alreadySelected = this.checkIfSelected(item)
        if (alreadySelected) {
          this.selectedItems = this.selectedItems.filter(
            (lookFolder) => lookFolder.id !== item.id
          )
        } else {
          this.selectedItems.push(item)
          if (item.videos_count) {
            await this.getFolderVideos(item.id, true)
          }
        }

        let block = false
        for (let selectedItem of this.selectedItems) {
          if (selectedItem.videos_count) {
            this.isVideo = false
            const isBlocked = await this.getFolderVideos(selectedItem.id, false)
            if (isBlocked) {
              block = true
              break
            }
          } else {
            this.isVideo = true
          }
        }
        this.blockFolder = block

        if (this.selectedItems.length === 1) {
          this.videoToBeDuplicated = this.selectedItems[0].id
        } else {
          this.videoToBeDuplicated = null
        }
      } finally {
        this.isLoading = false
      }
    },
    updateColumnNames() {
      const updatedNames = [
        i18n.t('message.ai.originalLang'),
        i18n.t('message.pages.video.subtitle'),
        i18n.t('message.ai.dubbings.dubbing'),
        i18n.t('message.pages.videosDataTable.header.size'),
        i18n.t('message.pages.videosDataTable.header.time'),
        i18n.t('message.pages.videosDataTable.header.lastModification'),
        'DRM',
      ]

      this.columns = this.columns.map((column, index) => ({
        ...column,
        name: updatedNames[index],
      }))
    },
    unselectAllItems() {
      this.selectedItems = []
    },
    selectAllItems() {
      let arr = []

      if (this.selectedItems.length !== this.allItems.length) {
        arr = [...this.allItems]
      }

      this.selectedItems = arr

      this.resetKeySelect()
    },
    setEmbed(option, videoItem) {
      const optionDefault = i18n
        .t('message.pages.videoEditModal.embedConfiguration.default')
        .toLowerCase()

      option = option.toLowerCase()
      const externalOption = this.isExternalSourceVideo ? option : false

      if (option === optionDefault) {
        option = this.embedSelected = 'general'
      }

      const [selectedOption] = this.embedOptions.filter(
        ({ value }) => value === option
      )

      if (externalOption) option = optionDefault

      const isExternalIdEmbed = ['123tapronto']
      const isSrcEmbed = [
        'cademi',
        'engaged',
        'monetizze',
        'blitzpay',
        'ensinio',
        'membertizze',
        'ticto',
      ]
      const isCodeEmbed = ['entrega-digital', 'creators-land']
      const isHLSEmbed = ['xgrow', 'greatpages', 'minervaead']
      const isRadioActiveFalse = Object.keys(this.embedOptionOrientations)
      const isRadioActiveTrue = this.embedOptions
        .map(({ value }) => {
          if (
            ![...isHLSEmbed, ...isRadioActiveFalse, 'general'].includes(value)
          )
            return value
        })
        .filter(Boolean)

      this.video = {}
      this.fetching = true
      this.showMenu = false
      this.hlsEmbed = false
      this.codeEmbed = false
      this.srcEmbed = false
      this.embedSelected = option
      this.embedTrademark = selectedOption.text

      if (isExternalIdEmbed.includes(option)) {
        this.externalIdEmbed = true
        if (videoItem) this.handleCopyExternalIdEmbed(videoItem)
      } else if (isSrcEmbed.includes(option)) {
        this.srcEmbed = true
        if (videoItem) this.handleCopySrcEmbed(videoItem)
      } else if (isCodeEmbed.includes(option)) {
        this.codeEmbed = true
        if (videoItem) this.handleCopyCodeEmbed(videoItem)
      } else if (isHLSEmbed.includes(option)) {
        this.hlsEmbed = true
        this.radioActive = 1

        if (videoItem) this.handleCopyHLSEmbed(videoItem)
      } else {
        if (isRadioActiveTrue.includes(option)) {
          this.videoCustomSize = true
          this.radioActive = 1
        } else if (isRadioActiveFalse.includes(option)) {
          const orientation = this.isLandscapeOrientation
            ? 'landscape'
            : 'portrait'
          const { width, height } =
            this.embedOptionOrientations[option][orientation]

          this.videoCustomSize = false
          this.radioActive = 0

          this.embedVideo.width = width
          this.embedVideo.height = height
        }

        Storage.setItem('width', this.embedVideo.width)
        Storage.setItem('height', this.embedVideo.height)

        this.fetching = false
        this.video = videoItem

        if (this.embed) this.handleShowLink(this.embed)
      }

      if (externalOption) option = externalOption
      Storage.setItem('embedType', option)
    },
    checkIfPageIsParent(parentId) {
      if (this.$route.params.id && this.$route.params.id === parentId) {
        return true
      } else {
        if (parentId === null || parentId === 'root') {
          return true
        }
      }
    },
    async checkIfHasDownloadUrl() {
      const downloaderServer = Storage.getItem('downloaderServer')
      if (downloaderServer) {
        this.downloadServer = downloaderServer
        return
      }
      await this.setDownloaderServer()
    },
    async setDownloaderServer() {
      try {
        const ret = await hostsApi.getHosts()
        let hostsArray = []
        for (let hostArea in ret.data.hosts) {
          for (let i = 0; i < hostArea.length; i++) {
            let host = ret.data.hosts[hostArea][i]
            if (host) {
              hostsArray.push(host)
            }
          }
        }
        let mainServer =
          hostsArray[Math.floor(Math.random() * hostsArray.length)]
        let splitMainServer = mainServer.split('-')
        let serverTag = splitMainServer[splitMainServer.length - 1]
        Storage.setItem('downloaderServer', serverTag)
        Storage.setItem('defaultDownloader', true)
        this.downloadServer = serverTag
      } catch (e) {
        Storage.setItem('downloaderServer', BACKUP_DOWNLOADER_SERVER)
        this.downloadServer = BACKUP_DOWNLOADER_SERVER
        console.error(e)
      }
      this.uppyUpdated++
    },
    handleLogs(item) {
      this.$root.$emit('openModalItemLogs', item)
    },
    async getFolderSize(
      folders,
      isSub = false,
      id = null,
      data = new Map(),
      visitedFolders = new Set()
    ) {
      for (const folder of folders) {
        if (visitedFolders.has(folder.id)) continue

        visitedFolders.add(folder.id)

        const videosIn = await window.db.videos
          .where({ folder_id: folder.id })
          .and(({ status }) => status !== 'DELETING')
          .toArray()

        let fData = { size: 0, length: 0 }

        for (const { storage_size, length } of videosIn) {
          fData.size += storage_size
          fData.length += length
        }

        if (!isSub) {
          data.set(folder.id, fData)

          if (id === null) id === folder.id
        } else {
          const pData = data.get(id) || {
            size: 0,
            length: 0,
          }

          pData.size += fData.size
          pData.length += fData.length

          data.set(id, pData)
        }

        const subFolders = this.mainFolders.filter(
          ({ parent_folder_id }) => parent_folder_id === folder.id
        )

        if (subFolders.length) {
          await this.getFolderSize(
            subFolders,
            true,
            id ?? folder.id,
            data,
            visitedFolders
          )
        }
      }

      return data
    },
    handleInput(event) {
      if (!this.bypassClickCloseMenu) {
        this.showMenu = event
      }
    },
    checkIfIsImport(video) {
      if (!video.description) return
      const desc = video.description
      let val = desc.includes('hotmart') || desc.includes('vimeo')
      return val
    },
    handleBanners(isRecapAvailable) {
      const hasClosedAiBanner = Storage.getItem('hasClosedAiBanner')
      const hasClosedRecapBanner = Storage.getItem('hasClosedRecapBanner')
      const hasClosedTrainingBanner = Storage.getItem('hasClosedTrainingBanner')
      const trainingBannerExpiry = Storage.getItem('trainingBannerExpiry')

      const hasClosedBlackFridayBanner =
        Storage.getItem('hasClosedBlackBanner') ?? false

      const blackBannerExpiry = Storage.getItem('blackFridayExpiry')

      this.bannersExpiredDelay =
        Storage.getItem('bannersInDelayExpery') ?? false

      if (this.bannersExpiredDelay) {
        this.bannersExpiredDelay = new Date(this.bannersExpiredDelay) <= now
      }

      const isBlackBannerExpired =
        blackBannerExpiry && new Date(blackBannerExpiry) <= now

      if (isBlackBannerExpired) {
        Storage.removeItem('hasClosedBlackFridayBanner')
        Storage.removeItem('blackFridayExpiry')
      }

      const now = new Date()
      const isTrainingBannerExpired =
        trainingBannerExpiry && new Date(trainingBannerExpiry) <= now
      if (isTrainingBannerExpired) {
        Storage.removeItem('hasClosedTrainingBanner')
        Storage.removeItem('trainingBannerExpiry')
      }

      let availableBannersToShow = []
      let canShowTrainingBanner =
        this.isPtBr && !this.isPandaUs && !hasClosedTrainingBanner
      if (!hasClosedAiBanner) availableBannersToShow.push('AI')
      if (!hasClosedBlackFridayBanner)
        availableBannersToShow.push('BLACK_FRIDAY')
      if (canShowTrainingBanner) availableBannersToShow.push('TRAINING')
      if (!hasClosedRecapBanner && isRecapAvailable)
        availableBannersToShow.push('RECAP')

      const bannersCount = availableBannersToShow.length
      if (bannersCount) {
        const randomIndex = Math.floor(Math.random() * bannersCount)
        this.showAiBanner = availableBannersToShow[randomIndex] === 'AI'
        this.showTrainingBanner =
          availableBannersToShow[randomIndex] === 'TRAINING'
        this.showRecapBanner = availableBannersToShow[randomIndex] === 'RECAP'
        this.showBlackFridayBanner =
          availableBannersToShow[randomIndex] === 'BLACK_FRIDAY'
      } else {
        this.showRecapBanner =
          this.showTrainingBanner =
          this.showAiBanner =
          this.showBlackFridayBanner =
            false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/data-tableV2';

.v-icon.mdi-checkbox-blank-outline {
  color: #d0d1d9 !important;
}

.text-truncate {
  max-width: 350px;
  display: inline-block;
  text-align: left;
}

@media (max-width: 1610px) {
  .text-truncate {
    max-width: 270px;
  }
}

.v-menu__content {
  border-radius: var(--spacing-01, 2px);
  background: var(--neutral-100-white, #fff);
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.15);
}

.v-list-item__content {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.v-list-item__title {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.caption {
  /* label/xs */
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%; /* 12px */
}

// .tooltip-container {
//   position: relative;
// }
// .tooltip-position-bot {
//   bottom: 100%;
//   margin-bottom: -10px;
// }
// .tooltip-position-top {
//   top: 100%;
//   margin-top: -10px;
// }

.emptyStateTitle {
  font-size: 20px;
  font-weight: 400;
}

.data-table__header-fixed {
  height: 45px;
  position: fixed;
  z-index: 2;
  border-radius: 0px;
  top: 0;
  left: 270px;
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.15) !important;
}

.emptyStateDesc {
  font-size: 14px;
  font-weight: 400;
}

.btnStyle {
  padding: 16px;
  border-radius: 8px;

  span {
    text-transform: none;
    letter-spacing: 0px;
    font-size: 14px;
    font-weight: 500;
    line-height: 150%; /* 21px */
  }
}

.empty__container {
  height: 65vh;
  display: flex;
  align-items: center;
}

.v-menu__content[data-v-56fb59d6] {
  border-radius: 8px;
}

// converting loading

.nalps {
  width: 16px;
  height: 16px;
  margin: -8px 0 0 -8px;
  border: 2px solid varFigma(neutral-80-lighter);
  border-radius: 50%;
  border-top: 2px solid varFigma(brand-60-base);
  -webkit-animation: spin 2s linear infinite;
  animation: spin 2s linear infinite;
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.blockedWarning {
  color: varFigma(feedback-danger-dark);
  display: flex;
  height: 18px;
  padding: 0px var(--spacing-02, 4px);
  justify-content: center;
  align-items: center;
  gap: var(--spacing-02, 4px);
  border-radius: var(--spacing-03, 8px);
  background: varFigma(feedback-danger-light);
}

// .tooltip-content {
//   position: absolute;
//   z-index: 9999 !important;
//   margin-left: -95px;
//   min-width: 200px;
//   padding: 5px;
//   background-color: rgba(97, 97, 97, 0.7);
//   color: #ffffff;
//   text-align: center;
//   border-radius: 6px;
//   white-space: normal;
//   word-wrap: break-word;
//   word-break: keep-all;
// }

.dupTooltip {
  width: 200px !important;
}

::v-deep body.scrollbar-light-mode ::-webkit-scrollbar-thumb {
  background-color: #d0d1d9 !important;
}

::v-deep body.scrollbar-dark-mode ::-webkit-scrollbar-thumb {
  background-color: #52577a !important;
}

::v-deep .virtual-scroller[data-v-7fd91582]:not(.page-mode) {
  padding-bottom: $spacing-11;
}

.selectedItensText {
  font-size: 12px;
  font-weight: 500;
}

.lang-flag {
  border-radius: 4px;
  margin: -16px 0 0 -24px !important;
  transform: scale(0.44) translateX(60%) translateY(38%) !important;
  -ms-transform: scale(0.44) translateX(60%) translateY(38%) !important;
  -webkit-transform: scale(0.44) translateX(60%) translateY(38%) !important;
  -moz-transform: scale(0.44) translateX(60%) translateY(38%) !important;
}

.pageInfo {
  color: var(--neutral-40-light, #636578);
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
}

.flagContainer {
  padding: 2px 8px;
  border-radius: var(--spacing-03, 8px);
  border: 1px solid varFigma(neutral-95-lightest);
  background: varFigma(neutral-100-white);
}

.flagContainer:hover {
  border: 1px solid var(--neutral-95-lightest, #a0acff);
  cursor: pointer;
}

.columnIcon {
  color: varFigma(neutral-40-light) !important;
}

.dotsRow {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dotsActive {
  border-radius: var(--spacing-02, 4px);
  background: var(--neutral-90, #e3e4e8);
}

.fixed-header {
  width: 100% !important;
  position: absolute !important;
  top: 125px !important;
  left: 0 !important;
  border-bottom: 1px solid var(--neutral-95-lightest, #edeef1) !important;
  background: var(--neutral-100-white, #fff) !important;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 20px 0px !important;
}

.floatMenu {
  background: red;
  position: absolute;
  bottom: 200px;
  left: 50%; /* Centraliza o elemento horizontalmente */
  transform: translateX(-50%); /* Ajusta o centro do elemento */
  z-index: 999;
}

.page-videos {
  &::v-deep {
    .page-view__content {
      padding-bottom: 0;
    }

    .item-line {
      border-bottom: 1px solid varFigma(neutral-95-lightest);

      &.rowIsDropOver {
        .rowSlot > .row:not(.rowIsDragged) {
          background-color: var(--v-brand-lighten2);
        }
      }

      .rowSlot:has(.tooltip-container) {
        overflow: visible !important;
      }

      .rowIsSelected {
        background-color: #ebeeff !important;
      }

      .rowIsDragged {
        opacity: 0.5;
      }

      .hideGhostImage {
        display: none;
      }
    }

    .virtualTable {
      .item-line {
        &.rowIsDropOver {
          .rowSlot > .row:not(.rowIsDragged) {
            background-color: var(--v-neutral-base);
          }
        }

        .rowIsSelected {
          background-color: var(--v-neutral-darken1) !important;
          &:hover {
            background-color: var(--v-neutral-darken1) !important;
          }
        }
      }
    }
  }
}

div.item-line:hover .hoveredClass {
  display: block;
}

div.item-line .hoveredClass {
  display: none;
}

.router-link {
  cursor: pointer;
}

.buttons {
  margin-bottom: $spacing-plus-1;

  display: grid;
  overflow-x: auto;

  grid-template-columns: repeat(5, auto);
  column-gap: $spacing-minus-2;
  row-gap: $spacing-minus-2;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    margin-bottom: initial;
  }
}

.itemDesc {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
}

// .v-tooltip__content.menuable__content__active {
//   opacity: 0.9;
//   width: 375px;
// }

.storageContainer {
  width: 200px !important;
}

.mousearea {
  position: relative;
  user-select: none;

  &__effect {
    position: absolute;
    z-index: 10;

    background: #3e50f729;
    border: 1px dotted var(--v-brand-base);

    pointer-events: none;
  }

  &__dragging {
    position: absolute;
    z-index: 10;

    display: none;
    width: 282px;
    padding: $spacing-base;
    background-color: var(--v-neutral-lighten4);

    border-radius: var(--spacing-02, 4px);
    border: 1px solid varFigma(neutral-90);
    box-shadow: 0px 2px 30px 0px rgba(0, 0, 0, 0.3);

    color: var(--v-neutral-lighten1);
    pointer-events: none;

    &--name {
      display: block;
      overflow: hidden;

      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &--counter {
      $size: $spacing-plus-1;

      position: absolute;
      top: -7.5px;
      right: -7.5px;
      z-index: 1;

      display: flex;
      padding: 0 8px;
      height: 20px;
      border-radius: 16px;
      background-color: varFigma(brand-90);

      align-items: center;
      justify-content: center;

      font-size: 80%;
      color: varFigma(brand-20-darkest);
      font-weight: 500;
    }

    &.cascate {
      box-shadow: 0px 2px 30px 0px rgba(0, 0, 0, 0.3), 4px 4px 0px 0px #fff,
        3px 3px 5px 0px rgba(0, 0, 0, 0.3);
    }

    &.shrink {
      display: block;
      transform-origin: center;
      animation: shrink 0.25s cubic-bezier(0, 0, 0.15, 1);
    }

    &.dark-theme {
      color: #585d83;
    }

    @keyframes shrink {
      from {
        width: 1000px;
        background-color: var(--v-brand-lighten2);
        opacity: 0.75;
      }
      to {
        width: 282px;
        background-color: var(--v-neutral-lighten4);
        border-radius: var(--spacing-02, 4px);
        border: 1px solid varFigma(neutral-90);
        box-shadow: 0px 2px 30px 0px rgba(0, 0, 0, 0.3);
        opacity: 1;
      }
    }
  }
}

.fixed-menu {
  max-height: 300px !important;
  overflow-y: auto !important;
}

.menuIcon {
  color: varFigma(neutral-20-light) !important;
}

.menu-list {
  max-height: inherit !important;
}
</style>

<style lang="scss">
.uploadInput {
  display: none;
}

.selectedRow {
  background: var(--v-progressBar-lighten-1);
}

.noIbar {
  cursor: default;
}

.truncate {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.playIconMargin {
  margin-left: 7px;
  margin-right: 5px;
}

.svgArrowUp {
  position: relative;
  bottom: 300px;
  right: 40px;
}

.selectTemplate .v-list {
  padding: 0px;
}

.clickableText {
  cursor: pointer;
}

.t-header {
  display: none;
}

.t-header span {
  font-weight: bold;
}

div.item-line:hover .hoveredClass {
  display: block;
}

div.item-line .hoveredClass {
  display: none;
}

.displayBlock {
  display: block;
}

.displayNone {
  display: none;
}

.danger-remove {
  color: #f44336;
}

@keyframes removesMenu {
  from {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0% 100%);
  }
  to {
    clip-path: polygon(50% 0, 50% 0, 50% 100%, 50% 100%);
  }
}

@keyframes appearsMenu {
  from {
    clip-path: polygon(50% 0, 50% 0, 50% 100%, 50% 100%);
  }
  to {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0% 100%);
  }
}

.pointerTable {
  cursor: pointer;
}

.text-underlined {
  position: relative;
}

.text-underlined:after {
  content: '';
  position: absolute;
  width: 100%;
  bottom: 1px;
  border-width: 0px 0px 1px;
  border-style: solid;
  left: 3px;
}

.deletedText {
  text-decoration: line-through solid #6f6f6f 0.2px;
}

.video-row {
  flex-wrap: nowrap !important;
}

@media screen and (max-width: 1114px) {
  .responsive-text {
    white-space: nowrap;
  }
}

@media screen and (max-width: 769px) {
  .responsive-text {
    white-space: normal;
  }
}
</style>
